/**
 * AI防災リスク診断ユーティリティ
 * ドローで囲んだポリゴンに対して、PMTilesから直接データを取得し分析
 */

import * as turf from '@turf/turf'
import type { Feature, Polygon, Point, MultiPolygon } from 'geojson'
import Pbf from 'pbf'
import { VectorTile } from '@mapbox/vector-tile'
import { PMTiles } from 'pmtiles'
import { fetchReinfolibAll, formatSingleResultForAi } from './reinfolibAnalyzer'
import muni from '@/js/muni'
import axios from 'axios'

// 型定義
interface TileCoord {
  x: number
  y: number
  z: number
}

// 人口ピラミッド用の年齢区分データ（6区分・メッシュベース）
interface PyramidAgeGroup {
  ageClass: string  // 年齢区分ラベル
  male: number      // 男性人口
  female: number    // 女性人口
}

// 詳細人口ピラミッド用の年齢区分データ（19区分・小地域融合）
interface DetailedPyramidAgeGroup {
  ageClass: string  // 年齢区分ラベル（0～4歳, 5～9歳, ...）
  male: number      // 男性人口
  female: number    // 女性人口
}

// 人口時系列データ（2005, 2010, 2015, 2020）
interface PopulationTimeSeries {
  year: number
  population: number
  elderlyRate: number    // 高齢化率（%）
  youngRate: number      // 年少率（%）
  workingAgeRate: number // 生産年齢率（%）
}

// 小地域詳細データ（超人口データ）
interface SyochiikiDetailData {
  cdArea: string           // 小地域コード
  areaName: string         // 地域名
  averageAge: number       // 平均年齢
  elderlyRate: number      // 高齢化率（%）
  detailedPyramid: DetailedPyramidAgeGroup[]  // 19区分ピラミッド
  timeSeries: PopulationTimeSeries[]          // 時系列データ
  // 125mメッシュ特有データ（小地域内全メッシュ集計）
  meshPopulation: number          // メッシュ集計人口
  youngPopulation: number         // 年少人口（0-14歳）
  workingAgePopulation: number    // 生産年齢人口（15-64歳）
  elderlyPopulation: number       // 老年人口（65歳以上）
  lateElderlyPopulation: number   // 後期高齢者人口（75歳以上）
  householdCount: number          // 世帯総数
  singleHouseholdCount: number    // 1人世帯数
  elderlySingleHouseholdCount: number // 高齢単身世帯数
  elderlyCoupleHouseholdCount: number // 高齢夫婦世帯数
  foreignPopulation: number       // 外国人人口
  meshCount: number               // 集計メッシュ数
  polygon?: Feature<Polygon>      // 小地域ポリゴン
}

interface PopulationResult {
  analyzed: boolean
  population: number
  populationRaw: number
  youngPopulation: number      // 年少人口（0-14歳）
  workingAgePopulation: number // 生産年齢人口（15-64歳）
  elderlyPopulation: number    // 老年人口（65歳以上）
  lateElderlyPopulation: number // 後期高齢者人口（75歳以上）
  foreignPopulation: number     // 外国人人口
  householdCount: number        // 世帯総数
  singleHouseholdCount: number  // 1人世帯数
  elderlySingleHouseholdCount: number // 高齢単身世帯数
  elderlyCoupleHouseholdCount: number // 高齢夫婦世帯数
  // ピラミッド用男女別データ
  maleTotal: number             // 男性総数
  femaleTotal: number           // 女性総数
  pyramidData: PyramidAgeGroup[] // 年齢区分別男女データ
  // 小地域融合データ（超人口データ）
  syochiikiDetail?: SyochiikiDetailData
  meshId?: string
  meshFeatures?: Feature<Polygon | MultiPolygon>[]
  areaKm2: number
  populationDensity: number
  meshCount: number
  totalMeshCount: number
  elapsedMs: number
}

interface HazardResult {
  analyzed: boolean
  hasRisk: boolean
  maxValue: string | null
  distribution: {
    value: string
    areaRatio: number // 0-1
    sampleCount: number
    color: string
  }[]
  totalSamples: number
  affectedSamples: number
  elapsedMs: number
}

interface AreaAnalysisResult {
  analyzed: boolean
  population?: PopulationResult
  hazards?: Record<string, HazardResult>
  reinfolibHazards?: ReinfolibHazardSummary
  elevation?: number | null
  medical?: {
    name: string | null
    distance: number | null
    layerId: string | null
  }
  address?: string | null
  geology?: GeologyData | null
  error?: string
}

interface ReinfolibHazardHit {
  api: string
  name: string
  layerId: string | null
  detail: string
}

interface ReinfolibHazardSummary {
  analyzed: boolean
  hitCount: number
  apiCount: number
  hits: ReinfolibHazardHit[]
  misses: { api: string; name: string; layerId: string | null }[]
  evacuation?: {
    count: number
    nearestName: string | null
    nearestDistance: number | null
    nearestCoords?: [number, number] | null
    nearestList?: { name: string; distance: number; coords?: [number, number] | null }[]
    walkingMinutes?: number | null
    drivingMinutes?: number | null
    layerId: string | null
  }
  error?: string
}

interface ReinfolibApiResult {
  api?: string
  name?: string
  properties?: Record<string, any>
  distance?: number
}

interface ReinfolibBatchResult {
  success?: boolean
  error?: string
  results?: ReinfolibApiResult[]
}

// PMTilesインスタンスのキャッシュ
const pmtilesCache: Record<string, PMTiles> = {}

// 125mメッシュ人口のPMTiles URL
const MESH_125M_URL = 'https://kenzkenz.net/pmtiles/mesh/125m/125m_full.pmtiles'

// 小地域PMTiles URL
const SYOCHIIKI_URL = 'https://kenzkenz.net/pmtiles/syochiiki/syochiiki.pmtiles'

const REINFOLIB_HAZARD_APIS = [
  { api: 'XKT016', name: '災害危険区域', layerId: 'oh-reinfolib-saigai-kiken' },
  { api: 'XKT025', name: '液状化発生傾向', layerId: 'oh-fj-ekijyoka' },
  { api: 'XKT026', name: '洪水浸水想定区域', layerId: 'oh-reinfolib-kozui' },
  { api: 'XKT027', name: '高潮浸水想定区域', layerId: 'oh-reinfolib-takashio' },
  { api: 'XKT028', name: '津波浸水想定', layerId: 'oh-reinfolib-tsunami' },
  { api: 'XKT029', name: '土砂災害警戒区域', layerId: 'oh-reinfolib-dosya' },
  { api: 'XKT021', name: '地すべり防止区域', layerId: 'oh-reinfolib-jisuberi' },
  { api: 'XKT022', name: '急傾斜地崩壊危険区域', layerId: 'oh-reinfolib-kyukeisha' },
  { api: 'XKT020', name: '大規模盛土造成地マップ', layerId: 'oh-reinfolib-morido' },
  { api: 'XST001', name: '災害履歴', layerId: 'oh-saigai-rireki' },
]
const REINFOLIB_EVACUATION_API = { api: 'XGT001', name: '指定緊急避難場所', layerId: 'oh-reinfolib-hinanbasho' }

const REINFOLIB_TILE_ZOOM = 14
const REINFOLIB_EVACUATION_TILE_URL = 'https://kenzkenz.net/myphp/proxy.php?api=XGT001&z={z}&x={x}&y={y}'
const MAX_EVA_TILE_COUNT = 16
// const MEDICAL_URL = 'https://kenzkenz.net/pmtiles/iryo/i.pmtiles'
// const MEDICAL_LAYER_ID = 'i'

async function fetchElevation(lng: number, lat: number): Promise<number | null> {
  try {
    const url = `https://cyberjapandata2.gsi.go.jp/general/dem/scripts/getelevation.php?lon=${lng}&lat=${lat}&outtype=JSON`
    const response = await fetch(url)
    const data = await response.json()
    if (data.elevation !== undefined && data.elevation !== null && data.elevation !== '-----') {
      const value = parseFloat(data.elevation)
      return Number.isNaN(value) ? null : value
    }
    return null
  } catch {
    return null
  }
}

async function fetchRouteDurationMinutes(
  start: [number, number],
  end: [number, number],
  profile: 'foot-hiking' | 'driving-car'
): Promise<number | null> {
  try {
    // URLSearchParams方式（プリフライト不要、XServerのnginx問題を回避）
    const params = new URLSearchParams()
    params.append('data', JSON.stringify({
      profile,
      coordinates: [start, end],
    }))
    const response = await fetch('/oh3/php/orsRoute.php', {
      method: 'POST',
      body: params,
    })
    if (!response.ok) return null
    const data = await response.json()
    const summary = data?.features?.[0]?.properties?.summary
    const duration = summary?.duration
    if (duration === undefined || duration === null) return null
    const minutes = Math.round(Number(duration) / 60)
    return Number.isFinite(minutes) ? minutes : null
  } catch {
    return null
  }
}

async function fetchAddress(lng: number, lat: number): Promise<string | null> {
  try {
    const url = `https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress?lon=${lng}&lat=${lat}`
    const response = await fetch(url)
    const data = await response.json()
    if (data?.results) {
      const muniCd = data.results.muniCd || ''
      const lv01Nm = data.results.lv01Nm || ''
      let prefName = ''
      let muniName = data.results.muniNm || ''
      if (muniCd && (muni as Record<string, string>)[muniCd]) {
        const parts = (muni as Record<string, string>)[muniCd].split(',')
        if (parts.length >= 4) {
          prefName = parts[1]
          muniName = parts[3]
        }
      }
      return (prefName + muniName + lv01Nm).trim() || null
    }
    return null
  } catch {
    return null
  }
}

// 地質データ型
interface GeologyData {
  formationAge: string    // 形成時代
  group: string           // グループ
  lithology: string       // 岩相
}

// シームレス地質図APIから地質データを取得
async function fetchGeology(lng: number, lat: number): Promise<GeologyData | null> {
  try {
    const url = `https://gbank.gsj.jp/seamless/v2/api/1.2/legend.json?point=${lat},${lng}`
    const response = await fetch(url)
    const data = await response.json()
    if (data?.symbol) {
      return {
        formationAge: data.formationAge_ja || '',
        group: data.group_ja || '',
        lithology: data.lithology_ja || '',
      }
    }
    return null
  } catch {
    console.warn('[AI防災リスク診断] 地質データ取得エラー')
    return null
  }
}

function pickReinfolibSampleCount(areaKm2: number): number {
  if (areaKm2 <= 1) return 1
  if (areaKm2 <= 10) return 3
  if (areaKm2 <= 50) return 5
  return 9
}

function buildReinfolibSamplePoints(polygon: Feature<Polygon>): [number, number][] {
  const bbox = turf.bbox(polygon) as [number, number, number, number]
  const [minLng, minLat, maxLng, maxLat] = bbox
  const lngs = [minLng, (minLng + maxLng) / 2, maxLng]
  const lats = [minLat, (minLat + maxLat) / 2, maxLat]

  const points: [number, number][] = []
  const center = turf.pointOnFeature(polygon).geometry.coordinates as [number, number]
  points.push(center)

  for (const lat of lats) {
    for (const lng of lngs) {
      const p = turf.point([lng, lat])
      if (turf.booleanPointInPolygon(p, polygon)) {
        points.push([lng, lat])
      }
    }
  }

  return points
}

function dedupePointsByTile(points: [number, number][], maxPoints: number): [number, number][] {
  const seen = new Set<string>()
  const result: [number, number][] = []

  for (const [lng, lat] of points) {
    const tile = lngLatToTile(lng, lat, REINFOLIB_TILE_ZOOM)
    const key = `${tile.z}/${tile.x}/${tile.y}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push([lng, lat])
    if (result.length >= maxPoints) break
  }

  return result
}

function getEvacuationName(result: ReinfolibApiResult): string {
  const props = result.properties || {}
  const fromProps = (
    props.facility_name_ja ||
    props.facility_name ||
    props.P20_003_ja ||
    props.P20_003 ||
    props.P20_002_ja ||
    props.P20_002 ||
    props.name_ja ||
    props.name ||
    ''
  )
  if (fromProps) return String(fromProps)
  if (result.name) return String(result.name)
  return ''
}

function parseCoordinate(value: any): number | null {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return num
}

function extractLngLatFromReinfolib(result: ReinfolibApiResult): [number, number] | null {
  const props = result.properties || {}
  const root: Record<string, any> = result as Record<string, any>
  const lonKeys = ['lon', 'lng', 'longitude', '経度', 'x', 'X', 'lon_deg', 'P20_006', 'P20_005']
  const latKeys = ['lat', 'latitude', '緯度', 'y', 'Y', 'lat_deg', 'P20_007', 'P20_004']

  for (const lonKey of lonKeys) {
    const lon = parseCoordinate(root[lonKey] ?? props[lonKey])
    if (lon === null) continue
    for (const latKey of latKeys) {
      const lat = parseCoordinate(root[latKey] ?? props[latKey])
      if (lat === null) continue
      if (lon < -180 || lon > 180 || lat < -90 || lat > 90) continue
      return [lon, lat]
    }
  }

  const geometry = (props.geometry || props.geom || root.geometry) as any
  if (geometry && Array.isArray(geometry.coordinates)) {
    const [lon, lat] = geometry.coordinates
    if (Number.isFinite(lon) && Number.isFinite(lat)) return [lon, lat]
  }

  return null
}

function getEvacuationNameFromProps(props: Record<string, any>): string {
  return (
    props.facility_name_ja ||
    props.facility_name ||
    props.P20_003_ja ||
    props.P20_003 ||
    props.P20_002_ja ||
    props.P20_002 ||
    props.name_ja ||
    props.name ||
    ''
  )
}

type EvacuationPoint = { name: string; coords: [number, number] }

function tileToEvacuationUrl(tile: TileCoord): string {
  return REINFOLIB_EVACUATION_TILE_URL
    .replace('{z}', String(tile.z))
    .replace('{x}', String(tile.x))
    .replace('{y}', String(tile.y))
}

function tilePointToLngLat(point: { x: number; y: number }, tile: TileCoord): [number, number] {
  return tilePixelToLngLat(point.x, point.y, tile)
}

async function fetchEvacuationPointsFromTiles(polygon: Feature<Polygon>): Promise<EvacuationPoint[] | null> {
  const bbox = turf.bbox(polygon) as [number, number, number, number]
  const [minLng, minLat, maxLng, maxLat] = bbox
  const tileA = lngLatToTile(minLng, minLat, REINFOLIB_TILE_ZOOM)
  const tileB = lngLatToTile(maxLng, maxLat, REINFOLIB_TILE_ZOOM)
  const minX = Math.min(tileA.x, tileB.x)
  const maxX = Math.max(tileA.x, tileB.x)
  const minY = Math.min(tileA.y, tileB.y)
  const maxY = Math.max(tileA.y, tileB.y)
  const tileCount = (maxX - minX + 1) * (maxY - minY + 1)
  if (tileCount > MAX_EVA_TILE_COUNT) return null

  const points: EvacuationPoint[] = []
  const seen = new Set<string>()

  for (let x = minX; x <= maxX; x += 1) {
    for (let y = minY; y <= maxY; y += 1) {
      const tile = { x, y, z: REINFOLIB_TILE_ZOOM }
      const url = tileToEvacuationUrl(tile)
      try {
        const res = await fetch(url)
        if (!res.ok) continue
        const buffer = await res.arrayBuffer()
        const vt = new VectorTile(new Pbf(buffer))
        const layer = vt.layers?.hits
        if (!layer) continue
        for (let i = 0; i < layer.length; i += 1) {
          const feature = layer.feature(i)
          const geom = feature.loadGeometry()
          const props = feature.properties || {}
          for (const part of geom) {
            for (const point of part) {
              const [lng, lat] = tilePointToLngLat(point, tile)
              const name = getEvacuationNameFromProps(props)
              const key = name ? name : `${lng.toFixed(6)},${lat.toFixed(6)}`
              if (seen.has(key)) continue
              seen.add(key)
              points.push({ name: name || '指定緊急避難場所', coords: [lng, lat] })
            }
          }
        }
      } catch (e) {
        continue
      }
    }
  }

  return points
}

async function getEvacuationStatsFromTiles(
  polygon: Feature<Polygon>
): Promise<{
  count: number | null
  nearestName: string | null
  nearestDistance: number | null
  nearestCoords: [number, number] | null
  nearestList: { name: string; distance: number; coords: [number, number] }[]
}> {
  const points = await fetchEvacuationPointsFromTiles(polygon)
  if (points === null) {
    return { count: null, nearestName: null, nearestDistance: null, nearestCoords: null, nearestList: [] }
  }
  if (points.length === 0) {
    return { count: 0, nearestName: null, nearestDistance: null, nearestCoords: null, nearestList: [] }
  }

  const center = turf.pointOnFeature(polygon)
  const pointsWithDistance: { name: string; distance: number; coords: [number, number]; feature: Feature<Point> }[] = []

  for (const point of points) {
    const feature = turf.point(point.coords)
    const distanceKm = turf.distance(center, feature, { units: 'kilometers' })
    const distanceM = Math.round(distanceKm * 1000)
    pointsWithDistance.push({
      name: point.name,
      distance: distanceM,
      coords: point.coords,
      feature,
    })
  }

  // ポリゴン内の避難場所をフィルタリング
  const pointFeatures = pointsWithDistance.map(p => p.feature)
  const within = turf.pointsWithinPolygon(turf.featureCollection(pointFeatures), polygon)
  const withinIndices = new Set(
    within.features.map(f => {
      const coords = (f.geometry as Point).coordinates
      return pointsWithDistance.findIndex(p => p.coords[0] === coords[0] && p.coords[1] === coords[1])
    })
  )

  // ポリゴン内の避難場所のみを距離順にソート
  const withinList = pointsWithDistance
    .filter((_, idx) => withinIndices.has(idx))
    .sort((a, b) => a.distance - b.distance)
    .map(p => ({ name: p.name, distance: p.distance, coords: p.coords }))

  const nearest = withinList.length > 0 ? withinList[0] : null

  return {
    count: withinList.length,
    nearestName: nearest ? nearest.name : null,
    nearestDistance: nearest ? nearest.distance : null,
    nearestCoords: nearest ? nearest.coords : null,
    nearestList: withinList,
  }
}

function countEvacuationWithinPolygon(results: ReinfolibApiResult[], polygon: Feature<Polygon>): number | null {
  const evacResults = results.filter(r => r.api === REINFOLIB_EVACUATION_API.api)
  if (evacResults.length === 0) return 0

  const seen = new Set<string>()
  const points: Feature<Point>[] = []
  let hasCoords = false

  for (const result of evacResults) {
    const coords = extractLngLatFromReinfolib(result)
    if (!coords) continue
    hasCoords = true
    const [lon, lat] = coords
    const name = getEvacuationName(result)
    const key = name ? name : `${lon},${lat}`
    if (seen.has(key)) continue
    seen.add(key)
    points.push(turf.point([lon, lat]))
  }
  if (!hasCoords) return null

  const pointCollection = turf.featureCollection(points)
  const within = turf.pointsWithinPolygon(pointCollection, polygon)
  return within.features.length
}

function summarizeEvacuation(
  results: ReinfolibApiResult[],
  polygon?: Feature<Polygon>,
  countOverride?: number | null
): {
  count: number
  nearestName: string | null
  nearestDistance: number | null
  nearestCoords: [number, number] | null
  nearestList: { name: string; distance: number; coords?: [number, number] | null }[]
  walkingMinutes: number | null
  drivingMinutes: number | null
  layerId: string | null
} {
  const evacResults = results.filter(r => r.api === REINFOLIB_EVACUATION_API.api)
  if (evacResults.length === 0) {
    return {
      count: 0,
      nearestName: null,
      nearestDistance: null,
      nearestCoords: null,
      nearestList: [],
      walkingMinutes: null,
      drivingMinutes: null,
      layerId: REINFOLIB_EVACUATION_API.layerId,
    }
  }

  const seenNames = new Set<string>()
  const unique = evacResults.filter(r => {
    const name = getEvacuationName(r)
    if (!name) return true
    if (seenNames.has(name)) return false
    seenNames.add(name)
    return true
  })

  const nearest = evacResults.reduce<ReinfolibApiResult | null>((best, current) => {
    if (current.distance === undefined || current.distance === null) return best
    if (!best || best.distance === undefined || best.distance === null) return current
    return current.distance < best.distance ? current : best
  }, null)

  const countInPolygon = polygon ? countEvacuationWithinPolygon(results, polygon) : null

  return {
    count: countOverride !== undefined && countOverride !== null
      ? countOverride
      : (countInPolygon !== null ? countInPolygon : unique.length),
    nearestName: nearest?.name ? String(nearest.name) : null,
    nearestDistance: nearest?.distance ?? null,
    nearestCoords: null,
    nearestList: [],
    walkingMinutes: null,
    drivingMinutes: null,
    layerId: REINFOLIB_EVACUATION_API.layerId,
  }
}

async function fetchNearestEvacuationsFromTile(
  point: Feature<Point>,
  limit = 3
): Promise<{ name: string; distance: number; coords: [number, number] }[]> {
  const [lng, lat] = point.geometry.coordinates
  const tile = lngLatToTile(lng, lat, REINFOLIB_TILE_ZOOM)
  const url = tileToEvacuationUrl(tile)
  try {
    const res = await fetch(url)
    if (!res.ok) return []
    const buffer = await res.arrayBuffer()
    const vt = new VectorTile(new Pbf(buffer))
    const layer = vt.layers?.hits
    if (!layer) return []
    const seen = new Set<string>()
    const candidates: { name: string; distance: number; coords: [number, number] }[] = []
    for (let i = 0; i < layer.length; i += 1) {
      const feature = layer.feature(i)
      const geom = feature.loadGeometry()
      const props = feature.properties || {}
      for (const part of geom) {
        for (const p of part) {
          const coords = tilePointToLngLat(p, tile)
          const distanceKm = turf.distance(point, turf.point(coords), { units: 'kilometers' })
          const distanceM = Math.round(distanceKm * 1000)
          const name = getEvacuationNameFromProps(props) || '名称未取得'
          const key = `${name}_${coords[0]}_${coords[1]}`
          if (seen.has(key)) continue
          seen.add(key)
          candidates.push({ name, distance: distanceM, coords })
        }
      }
    }
    return candidates.sort((a, b) => a.distance - b.distance).slice(0, limit)
  } catch (_) {
    return []
  }
}

async function fetchNearestEvacuationFromTile(
  point: Feature<Point>
): Promise<{ name: string; distance: number; coords: [number, number] } | null> {
  const [lng, lat] = point.geometry.coordinates
  const tile = lngLatToTile(lng, lat, REINFOLIB_TILE_ZOOM)
  const url = tileToEvacuationUrl(tile)
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const buffer = await res.arrayBuffer()
    const vt = new VectorTile(new Pbf(buffer))
    const layer = vt.layers?.hits
    if (!layer) return null
    let best: { name: string; distance: number; coords: [number, number] } | null = null
    for (let i = 0; i < layer.length; i += 1) {
      const feature = layer.feature(i)
      const geom = feature.loadGeometry()
      const props = feature.properties || {}
      for (const part of geom) {
        for (const p of part) {
          const coords = tilePointToLngLat(p, tile)
          const distanceKm = turf.distance(point, turf.point(coords), { units: 'kilometers' })
          const distanceM = Math.round(distanceKm * 1000)
          const name = getEvacuationNameFromProps(props) || '名称未取得'
          if (!best || distanceM < best.distance) {
            best = { name, distance: distanceM, coords }
          }
        }
      }
    }
    return best
  } catch (_) {
    return null
  }
}

async function computeNearestEvacuationsForPoint(
  point: Feature<Point>,
  results: ReinfolibApiResult[],
  limit = 3
): Promise<{ name: string; distance: number; coords?: [number, number] | null }[]> {
  const evacResults = results.filter(r => r.api === REINFOLIB_EVACUATION_API.api)
  const candidates: { name: string; distance: number; coords?: [number, number] | null }[] = []
  const seen = new Set<string>()

  for (const result of evacResults) {
    const name = getEvacuationName(result) || '名称未取得'
    const coords = extractLngLatFromReinfolib(result)
    if (!coords) continue
    const distanceKm = turf.distance(point, turf.point(coords), { units: 'kilometers' })
    const distance = Math.round(distanceKm * 1000)
    const key = `${name}_${coords[0]}_${coords[1]}`
    if (seen.has(key)) continue
    seen.add(key)
    candidates.push({ name, distance, coords })
  }

  if (candidates.length < limit) {
    const tileCandidates = await fetchNearestEvacuationsFromTile(point, limit)
    for (const cand of tileCandidates) {
      const key = `${cand.name}_${cand.coords[0]}_${cand.coords[1]}`
      if (seen.has(key)) continue
      seen.add(key)
      candidates.push(cand)
      if (candidates.length >= limit) break
    }
  }

  return candidates.sort((a, b) => a.distance - b.distance).slice(0, limit)
}

async function computeNearestEvacuationForPoint(
  point: Feature<Point>,
  results: ReinfolibApiResult[]
): Promise<{ name: string; distance: number; coords?: [number, number] | null } | null> {
  const list = await computeNearestEvacuationsForPoint(point, results, 1)
  if (list.length > 0) return list[0]
  return null
}

/**
 * 緯度経度からタイル座標を計算
 */
function lngLatToTile(lng: number, lat: number, zoom: number): TileCoord {
  const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom))
  const latRad = lat * Math.PI / 180
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom))
  return { x, y, z: zoom }
}

function getNeighborTiles(center: TileCoord, radius: number): TileCoord[] {
  const tiles: TileCoord[] = []
  const limit = Math.pow(2, center.z)
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dy = -radius; dy <= radius; dy++) {
      const x = center.x + dx
      const y = center.y + dy
      if (x < 0 || x >= limit || y < 0 || y >= limit) continue
      tiles.push({ x, y, z: center.z })
    }
  }
  return tiles
}

/**
 * MVTピクセル座標を緯度経度に変換
 */
function tilePixelToLngLat(pixelX: number, pixelY: number, tile: TileCoord): [number, number] {
  const n = Math.pow(2, tile.z)
  const lng = (tile.x + pixelX / 4096) / n * 360 - 180
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * (tile.y + pixelY / 4096) / n)))
  const lat = latRad * 180 / Math.PI
  return [lng, lat]
}

function normalizePolygonGeometries(geoms: Array<Feature<Polygon | MultiPolygon>>): Feature<Polygon | MultiPolygon> | null {
  const polygons: Polygon['coordinates'][] = []
  let props: Record<string, any> = {}

  for (const geom of geoms) {
    if (!geom) continue
    if (!props || Object.keys(props).length === 0) {
      props = (geom.properties || {}) as Record<string, any>
    }
    const geometry = geom.geometry
    if (!geometry) continue
    if (geometry.type === 'Polygon') {
      polygons.push(geometry.coordinates)
    } else if (geometry.type === 'MultiPolygon') {
      polygons.push(...geometry.coordinates)
    }
  }

  if (polygons.length === 0) return null
  if (polygons.length === 1) {
    return {
      type: 'Feature',
      properties: props,
      geometry: {
        type: 'Polygon',
        coordinates: polygons[0],
      },
    }
  }
  return {
    type: 'Feature',
    properties: props,
    geometry: {
      type: 'MultiPolygon',
      coordinates: polygons,
    },
  }
}

/**
 * MVTフィーチャーをGeoJSONポリゴンに変換
 */
function featureToGeoJSON(feature: any, tile: TileCoord): Feature<Polygon> {
  const geom = feature.loadGeometry()
  const coordinates: [number, number][][] = []

  for (const ring of geom) {
    const ringCoords: [number, number][] = ring.map((p: { x: number; y: number }) =>
      tilePixelToLngLat(p.x, p.y, tile)
    )
    // ポリゴンを閉じる
    if (ringCoords.length > 0) {
      const first = ringCoords[0]
      const last = ringCoords[ringCoords.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ringCoords.push([first[0], first[1]])
      }
    }
    coordinates.push(ringCoords)
  }

  return {
    type: 'Feature',
    properties: feature.properties,
    geometry: {
      type: 'Polygon',
      coordinates: coordinates
    }
  }
}

function featureToPointGeoJSON(feature: any, tile: TileCoord): Feature<Point> | null {
  const geom = feature.loadGeometry()
  if (!geom || geom.length === 0 || geom[0].length === 0) return null
  const point = geom[0][0]
  const [lng, lat] = tilePixelToLngLat(point.x, point.y, tile)
  return {
    type: 'Feature',
    properties: feature.properties,
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
  }
}

function getMedicalName(props: Record<string, any>): string | null {
  const candidates = [
    props.P04_002_ja,
    props.P04_002,
    props.P04_001_name_ja,
    props.name,
  ]
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

// async function findNearestMedicalPoint(lng: number, lat: number): Promise<{ name: string | null; distance: number | null }> {
//   if (!pmtilesCache[MEDICAL_URL]) {
//     pmtilesCache[MEDICAL_URL] = new PMTiles(MEDICAL_URL)
//   }
//   const pmtiles = pmtilesCache[MEDICAL_URL]
//
//   let zoom = 14
//   try {
//     const header = await pmtiles.getHeader()
//     zoom = header.maxZoom
//   } catch (e) {
//     console.warn('[AI防災リスク診断] 医療機関ヘッダー取得エラー:', e)
//   }
//
//   const centerTile = lngLatToTile(lng, lat, zoom)
//   const basePoint = turf.point([lng, lat])
//   let bestName: string | null = null
//   let bestDistance: number | null = null
//
//   for (let radius = 0; radius <= 2; radius++) {
//     const tiles = getNeighborTiles(centerTile, radius)
//     for (const tile of tiles) {
//       let tileData: any = null
//       try {
//         tileData = await pmtiles.getZxy(tile.z, tile.x, tile.y)
//       } catch (e) {
//         continue
//       }
//       if (!tileData?.data) continue
//       const vt = new VectorTile(new Pbf(tileData.data))
//       const layer = vt.layers[MEDICAL_LAYER_ID]
//       if (!layer) continue
//       for (let i = 0; i < layer.length; i++) {
//         const feature = layer.feature(i)
//         const props = feature.properties || {}
//         const kind = Number(props.P04_001)
//         if (kind !== 1 && kind !== 2) continue
//         const pointFeature = featureToPointGeoJSON(feature, tile)
//         if (!pointFeature) continue
//         const distKm = turf.distance(basePoint, pointFeature, { units: 'kilometers' })
//         const distM = distKm * 1000
//         if (bestDistance === null || distM < bestDistance) {
//           bestDistance = distM
//           bestName = getMedicalName(props) || '名称未取得'
//         }
//       }
//     }
//     if (bestDistance !== null) {
//       break
//     }
//   }
//
//   return { name: bestName, distance: bestDistance }
// }

function extractNearestReinfolibMedical(results: ReinfolibApiResult[]): { name: string | null; distance: number | null } {
  const candidates = results
    .filter((r: ReinfolibApiResult) => r.api === 'XKT010')
    .filter((r: ReinfolibApiResult) => r.distance !== undefined && r.distance !== null)
  if (candidates.length === 0) {
    return { name: null, distance: null }
  }
  const best = candidates.reduce<ReinfolibApiResult>((prev, current) => {
    if (prev.distance === undefined || prev.distance === null) return current
    if (current.distance === undefined || current.distance === null) return prev
    return current.distance < prev.distance ? current : prev
  }, candidates[0])
  const props = best.properties || {}
  return {
    name: getMedicalName(props) || '名称未取得',
    distance: best.distance ?? null,
  }
}

/**
 * バウンディングボックスをカバーするタイル群を取得
 */
function getTilesForBbox(bbox: [number, number, number, number], zoom: number): TileCoord[] {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const minTile = lngLatToTile(minLng, maxLat, zoom) // 北西
  const maxTile = lngLatToTile(maxLng, minLat, zoom) // 南東

  const tiles: TileCoord[] = []
  for (let x = minTile.x; x <= maxTile.x; x++) {
    for (let y = minTile.y; y <= maxTile.y; y++) {
      tiles.push({ x, y, z: zoom })
    }
  }
  return tiles
}

/**
 * 座標から小地域ポリゴンを取得
 */
export async function getSyochiikiPolygon(lng: number, lat: number): Promise<{
  cdArea: string
  areaName: string
  polygon: Feature<Polygon>
} | null> {
  const zoom = 14
  const tile = lngLatToTile(lng, lat, zoom)
  const pixelPoint = lngLatToTilePixel(lng, lat, zoom)

  if (!pmtilesCache[SYOCHIIKI_URL]) {
    pmtilesCache[SYOCHIIKI_URL] = new PMTiles(SYOCHIIKI_URL)
  }
  const pmtiles = pmtilesCache[SYOCHIIKI_URL]

  try {
    const tileData = await pmtiles.getZxy(tile.z, tile.x, tile.y)
    if (!tileData?.data) return null

    const vt = new VectorTile(new Pbf(tileData.data))
    const layer = vt.layers['polygon']
    if (!layer) return null

    // MVTタイルのextentは通常4096、pixelPointは256ベースなのでスケール変換
    const extent = layer.extent || 4096
    const scaledPixelX = pixelPoint.pixelX * extent / 256
    const scaledPixelY = pixelPoint.pixelY * extent / 256

    for (let i = 0; i < layer.length; i++) {
      const feature = layer.feature(i)
      const geom = feature.loadGeometry()

      for (const ring of geom) {
        const coords = ring.map((p: { x: number; y: number }) => [p.x, p.y])
        if (pointInPolygonCheck([scaledPixelX, scaledPixelY], coords)) {
          const props = feature.properties
          const keyCode = props.KEY_CODE ? String(props.KEY_CODE) : ''
          const areaName = props.S_NAME ? String(props.S_NAME) : ''

          // ポリゴンをGeoJSONに変換
          const geoJSON = featureToGeoJSON(feature, tile)

          return {
            cdArea: keyCode,
            areaName,
            polygon: geoJSON as Feature<Polygon>
          }
        }
      }
    }
  } catch (e) {
    console.warn('[小地域ポリゴン取得] エラー:', e)
  }
  return null
}

/**
 * ポイントインポリゴン判定（タイル座標用）
 */
function pointInPolygonCheck(point: number[], polygon: number[][]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    if (((yi > point[1]) !== (yj > point[1])) && (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  return inside
}

/**
 * 超人口データを取得（小地域ベース + 125mメッシュ融合）
 * 1. 座標から小地域ポリゴンを取得
 * 2. 小地域ポリゴン内の全125mメッシュを集計（世帯・外国人）
 * 3. 小地域の詳細ピラミッド・時系列をAPI取得
 */
async function fetchSyochiikiDetailData(
  lng: number,
  lat: number
): Promise<SyochiikiDetailData | null> {

  try {
    // 1. 座標から小地域ポリゴンを取得
    const syochiikiResult = await getSyochiikiPolygon(lng, lat)
    if (!syochiikiResult) {
      return null
    }

    const { cdArea, areaName, polygon } = syochiikiResult
    const cityCode = cdArea.slice(0, 5)
    const azaCode = cdArea.slice(5)

    // 2. 小地域ポリゴン内の全125mメッシュを集計
    const meshResult = await analyzePopulation125m(polygon)

    // 3. 小地域の詳細ピラミッドデータを取得（2020年）
    const pyramid2020Response = await axios.get('https://kenzkenz.xsrv.jp/open-hinata/php/pyramid.php', {
      params: { cityCode, azaCode }
    })

    if (!pyramid2020Response.data || pyramid2020Response.data.error) {
      return null
    }

    // 小地域の総人口と各種データを取得
    let syochiikiTotalPopulation = 0
    let averageAge = 0
    let elderlyPopulation = 0
    let youngPopulation = 0
    let workingAgePopulation = 0
    const maleData: Record<string, number> = {}
    const femaleData: Record<string, number> = {}

    pyramid2020Response.data.forEach((v: any) => {
      if (v.男女 === '総数') {
        syochiikiTotalPopulation = Number(v['総数']) || 0
        averageAge = Number(v['平均年齢']) || 0
        elderlyPopulation = Number(v['65歳以上']) || 0
        youngPopulation = Number(v['15歳未満']) || 0
        workingAgePopulation = Number(v['15～64歳']) || 0
      } else if (v.男女 === '男') {
        maleData['0～4歳'] = Number(v['0～4歳']) || 0
        maleData['5～9歳'] = Number(v['5～9歳']) || 0
        maleData['10～14歳'] = Number(v['10～14歳']) || 0
        maleData['15～19歳'] = Number(v['15～19歳']) || 0
        maleData['20～24歳'] = Number(v['20～24歳']) || 0
        maleData['25～29歳'] = Number(v['25～29歳']) || 0
        maleData['30～34歳'] = Number(v['30～34歳']) || 0
        maleData['35～39歳'] = Number(v['35～39歳']) || 0
        maleData['40～44歳'] = Number(v['40～44歳']) || 0
        maleData['45～49歳'] = Number(v['45～49歳']) || 0
        maleData['50～54歳'] = Number(v['50～54歳']) || 0
        maleData['55～59歳'] = Number(v['55～59歳']) || 0
        maleData['60～64歳'] = Number(v['60～64歳']) || 0
        maleData['65～69歳'] = Number(v['65～69歳']) || 0
        maleData['70～74歳'] = Number(v['70～74歳']) || 0
        maleData['75～79歳'] = Number(v['75～79歳']) || 0
        maleData['80～84歳'] = Number(v['80～84歳']) || 0
        maleData['85～89歳'] = Number(v['85～89歳']) || 0
        maleData['90歳以上'] = Number(v['90歳以上']) || 0
      } else if (v.男女 === '女') {
        femaleData['0～4歳'] = Number(v['0～4歳']) || 0
        femaleData['5～9歳'] = Number(v['5～9歳']) || 0
        femaleData['10～14歳'] = Number(v['10～14歳']) || 0
        femaleData['15～19歳'] = Number(v['15～19歳']) || 0
        femaleData['20～24歳'] = Number(v['20～24歳']) || 0
        femaleData['25～29歳'] = Number(v['25～29歳']) || 0
        femaleData['30～34歳'] = Number(v['30～34歳']) || 0
        femaleData['35～39歳'] = Number(v['35～39歳']) || 0
        femaleData['40～44歳'] = Number(v['40～44歳']) || 0
        femaleData['45～49歳'] = Number(v['45～49歳']) || 0
        femaleData['50～54歳'] = Number(v['50～54歳']) || 0
        femaleData['55～59歳'] = Number(v['55～59歳']) || 0
        femaleData['60～64歳'] = Number(v['60～64歳']) || 0
        femaleData['65～69歳'] = Number(v['65～69歳']) || 0
        femaleData['70～74歳'] = Number(v['70～74歳']) || 0
        femaleData['75～79歳'] = Number(v['75～79歳']) || 0
        femaleData['80～84歳'] = Number(v['80～84歳']) || 0
        femaleData['85～89歳'] = Number(v['85～89歳']) || 0
        femaleData['90歳以上'] = Number(v['90歳以上']) || 0
      }
    })

    // 詳細ピラミッド（小地域の値をそのまま使用）
    const ageClasses = [
      '0～4歳', '5～9歳', '10～14歳', '15～19歳', '20～24歳', '25～29歳',
      '30～34歳', '35～39歳', '40～44歳', '45～49歳', '50～54歳', '55～59歳',
      '60～64歳', '65～69歳', '70～74歳', '75～79歳', '80～84歳', '85～89歳', '90歳以上'
    ]
    const detailedPyramid: DetailedPyramidAgeGroup[] = ageClasses.map(ageClass => ({
      ageClass,
      male: maleData[ageClass] || 0,
      female: femaleData[ageClass] || 0
    }))

    // 4. 時系列データを取得
    const timeSeries: PopulationTimeSeries[] = []
    const elderlyRate2020 = syochiikiTotalPopulation > 0 ? (elderlyPopulation / syochiikiTotalPopulation) * 100 : 0
    const youngRate2020 = syochiikiTotalPopulation > 0 ? (youngPopulation / syochiikiTotalPopulation) * 100 : 0
    const workingAgeRate2020 = syochiikiTotalPopulation > 0 ? (workingAgePopulation / syochiikiTotalPopulation) * 100 : 0

    timeSeries.push({
      year: 2020,
      population: syochiikiTotalPopulation,
      elderlyRate: Math.round(elderlyRate2020 * 10) / 10,
      youngRate: Math.round(youngRate2020 * 10) / 10,
      workingAgeRate: Math.round(workingAgeRate2020 * 10) / 10
    })

    // 過去データを取得
    for (const year of [2015, 2010, 2005]) {
      try {
        const response = await axios.get('https://kenzkenz.xsrv.jp/open-hinata/php/pyramid2015.php', {
          params: { cityCode, azaCode, year }
        })
        if (response.data && !response.data.error && response.data.length > 0) {
          const v = response.data[0]
          const pop = Number(v['総数']) || 0
          const over65 = Number(v['65歳以上']) || 0
          const under15 = Number(v['15歳未満']) || 0
          const seisan = Number(v['15～64歳']) || 0
          const eRate = pop > 0 ? (over65 / pop) * 100 : 0
          const yRate = pop > 0 ? (under15 / pop) * 100 : 0
          const wRate = pop > 0 ? (seisan / pop) * 100 : 0
          timeSeries.push({
            year,
            population: pop,
            elderlyRate: Math.round(eRate * 10) / 10,
            youngRate: Math.round(yRate * 10) / 10,
            workingAgeRate: Math.round(wRate * 10) / 10
          })
        }
      } catch (e) {
        console.warn(`[超人口データ] ${year}年データ取得エラー:`, e)
      }
    }
    timeSeries.sort((a, b) => a.year - b.year)


    // 後期高齢者は小地域国勢調査から計算
    const lateElderlyPopulation = (maleData['75～79歳'] || 0) + (maleData['80～84歳'] || 0) + (maleData['85～89歳'] || 0) + (maleData['90歳以上'] || 0)
      + (femaleData['75～79歳'] || 0) + (femaleData['80～84歳'] || 0) + (femaleData['85～89歳'] || 0) + (femaleData['90歳以上'] || 0)

    return {
      cdArea,
      areaName: areaName || '',
      averageAge,
      elderlyRate: Math.round(elderlyRate2020 * 10) / 10,
      detailedPyramid,
      timeSeries,
      // 人口は小地域国勢調査から
      meshPopulation: syochiikiTotalPopulation,
      youngPopulation,           // 小地域国勢調査
      workingAgePopulation,      // 小地域国勢調査
      elderlyPopulation,         // 小地域国勢調査
      lateElderlyPopulation,     // 小地域国勢調査から計算
      // 世帯・外国人は125mメッシュから
      householdCount: meshResult.householdCount,
      singleHouseholdCount: meshResult.singleHouseholdCount,
      elderlySingleHouseholdCount: meshResult.elderlySingleHouseholdCount,
      elderlyCoupleHouseholdCount: meshResult.elderlyCoupleHouseholdCount,
      foreignPopulation: meshResult.foreignPopulation,
      meshCount: meshResult.meshCount,
      polygon
    }
  } catch (e) {
    console.error('[超人口データ] エラー:', e)
    return null
  }
}

/**
 * ポリゴンエリア内の125mメッシュ人口を取得（按分計算）
 */
export async function analyzePopulation125m(polygon: Feature<Polygon>): Promise<PopulationResult> {

  const startTime = performance.now()

  // ポリゴンのbboxを取得
  const bbox = turf.bbox(polygon) as [number, number, number, number]

  // ポリゴンの面積を計算（km²）
  const polygonAreaKm2 = turf.area(polygon) / 1_000_000

  // PMTilesインスタンスを取得
  if (!pmtilesCache[MESH_125M_URL]) {
    pmtilesCache[MESH_125M_URL] = new PMTiles(MESH_125M_URL)
  }
  const pmtiles = pmtilesCache[MESH_125M_URL]

  // PMTilesのヘッダーを確認してズームレベル範囲を取得
  let zoom = 15 // デフォルト
  try {
    const header = await pmtiles.getHeader()
    // maxZoomを使用（PMTilesは通常maxZoomでデータが格納されている）
    zoom = header.maxZoom
  } catch (e) {
    console.warn('[AI防災リスク診断] ヘッダー取得エラー:', e)
  }
  const tiles = getTilesForBbox(bbox, zoom)

  // 全タイルを並列取得
  const tilePromises = tiles.map(async (tile) => {
    try {
      const tileData = await pmtiles.getZxy(tile.z, tile.x, tile.y)
      if (!tileData || !tileData.data) {
        return { tile, features: [] as { feature: any; tile: TileCoord }[] }
      }


      const pbf = new Pbf(new Uint8Array(tileData.data))
      const vectorTile = new VectorTile(pbf)

      // 利用可能なレイヤーをログ
      const layerNames = Object.keys(vectorTile.layers || {})

      const features: { feature: any; tile: TileCoord }[] = []
      if (vectorTile.layers && vectorTile.layers.polygon) {
        const layer = vectorTile.layers.polygon
        for (let i = 0; i < layer.length; i++) {
          const feature = layer.feature(i)
          // 最初の数件のプロパティをログ
          if (i < 3) {
          }
          features.push({ feature, tile })
        }
      } else {
      }
      return { tile, features }
    } catch (error) {
      console.warn(`[AI防災リスク診断] タイル取得エラー: z=${tile.z}, x=${tile.x}, y=${tile.y}`, error)
      return { tile, features: [] as { feature: any; tile: TileCoord }[] }
    }
  })

  const tileResults = await Promise.all(tilePromises)

  // メッシュIDごとにジオメトリを集約（タイル境界での欠けを防ぐ）
  const meshGeoms = new Map<string, { properties: Record<string, any>; geoms: Feature<Polygon | MultiPolygon>[] }>()

  // 結果集計
  let totalPopulation = 0
  let totalYoungPopulation = 0      // 年少人口（0-14歳）
  let totalWorkingAgePopulation = 0 // 生産年齢人口（15-64歳）
  let totalElderlyPopulation = 0    // 老年人口（65歳以上）
  let totalLateElderlyPopulation = 0 // 後期高齢者人口（75歳以上）
  let totalForeignPopulation = 0     // 外国人人口
  let totalHouseholdCount = 0        // 世帯総数
  let totalSingleHouseholdCount = 0  // 1人世帯数
  let totalElderlySingleHouseholdCount = 0 // 高齢単身世帯数
  let totalElderlyCoupleHouseholdCount = 0 // 高齢夫婦世帯数
  // ピラミッド用男女別集計
  let totalMale = 0              // 男性総数
  let totalFemale = 0            // 女性総数
  let totalYoungMale = 0         // 0-14歳男性
  let totalYoungFemale = 0       // 0-14歳女性
  let totalWorkingMale = 0       // 15-64歳男性
  let totalWorkingFemale = 0     // 15-64歳女性
  let totalElderly65Male = 0     // 65歳以上男性
  let totalElderly65Female = 0   // 65歳以上女性
  let totalElderly75Male = 0     // 75歳以上男性
  let totalElderly75Female = 0   // 75歳以上女性
  let totalElderly85Male = 0     // 85歳以上男性
  let totalElderly85Female = 0   // 85歳以上女性
  let totalElderly95Male = 0     // 95歳以上男性
  let totalElderly95Female = 0   // 95歳以上女性
  let meshCount = 0
  let intersectedMeshCount = 0
  const usedMeshFeatures: Feature<Polygon | MultiPolygon>[] = []


  // 全フィーチャーを処理
  for (const { tile, features } of tileResults) {
    for (const { feature } of features) {
      const properties = feature.properties

      // GeoJSONに変換
      let meshGeoJSON: Feature<Polygon>
      try {
        meshGeoJSON = featureToGeoJSON(feature, tile)
      } catch (e) {
        console.warn('[AI防災リスク診断] GeoJSON変換エラー', e)
        continue
      }
      const meshId = properties.KEY_CODE || properties.id || `${tile.z}-${tile.x}-${tile.y}`
      const entry = meshGeoms.get(meshId)
      if (entry) {
        entry.geoms.push(meshGeoJSON)
      } else {
        meshGeoms.set(meshId, { properties, geoms: [meshGeoJSON] })
      }
    }
  }

  meshCount = meshGeoms.size

  for (const [meshId, entry] of meshGeoms.entries()) {
    const meshFeature = normalizePolygonGeometries(entry.geoms)
    if (!meshFeature) continue

    // ポリゴンとの交差判定
    let intersects = false
    try {
      intersects = turf.booleanIntersects(polygon, meshFeature)
    } catch (e) {
      continue
    }

    if (!intersects) {
      continue
    }

    intersectedMeshCount++

    // 按分計算
    // 125mメッシュの人口プロパティ
    const properties = entry.properties || {}
    const population = Number(properties.T001225001) || 0        // 総人口
    const youngPop = Number(properties.T001225004) || 0          // 年少人口（0-14歳）
    const workingAgePop = Number(properties.T001225010) || 0     // 生産年齢人口（15-64歳）
    const elderlyPop = Number(properties.T001225019) || 0        // 老年人口（65歳以上）
    const lateElderlyPop = Number(properties.T001225022) || 0    // 後期高齢者人口（75歳以上）
    const foreignPop = Number(properties.T001225031) || 0        // 外国人人口
    const householdCnt = Number(properties.T001225034) || 0      // 世帯総数
    const singleHouseholdCnt = Number(properties.T001225036) || 0 // 1人世帯数
    const elderlySingleCnt = Number(properties.T001225049) || 0  // 高齢単身世帯数
    const elderlyCoupleCnt = Number(properties.T001225050) || 0  // 高齢夫婦世帯数
    // ピラミッド用男女別データ
    const male = Number(properties.T001225002) || 0              // 男性総数
    const female = Number(properties.T001225003) || 0            // 女性総数
    const youngMale = Number(properties.T001225005) || 0         // 0-14歳男性
    const youngFemale = Number(properties.T001225006) || 0       // 0-14歳女性
    const workingMale = Number(properties.T001225011) || 0       // 15-64歳男性
    const workingFemale = Number(properties.T001225012) || 0     // 15-64歳女性
    const elderly65Male = Number(properties.T001225020) || 0     // 65歳以上男性
    const elderly65Female = Number(properties.T001225021) || 0   // 65歳以上女性
    const elderly75Male = Number(properties.T001225023) || 0     // 75歳以上男性
    const elderly75Female = Number(properties.T001225024) || 0   // 75歳以上女性
    const elderly85Male = Number(properties.T001225026) || 0     // 85歳以上男性
    const elderly85Female = Number(properties.T001225027) || 0   // 85歳以上女性
    const elderly95Male = Number(properties.T001225029) || 0     // 95歳以上男性
    const elderly95Female = Number(properties.T001225030) || 0   // 95歳以上女性

    if (intersectedMeshCount <= 5) {
    }
    if (population === 0) {
      continue
    }

    try {
      // メッシュの面積
      const meshArea = turf.area(meshFeature)
      if (meshArea === 0) {
        continue
      }

      // 交差部分を計算（turf v7 API）
      const intersection = turf.intersect(turf.featureCollection([polygon, meshFeature]))

      if (intersection) {
        const intersectArea = turf.area(intersection)
        const ratio = Math.min(intersectArea / meshArea, 1.0) // 幾何誤差で1超を防止
        totalPopulation += population * ratio
        totalYoungPopulation += youngPop * ratio
        totalWorkingAgePopulation += workingAgePop * ratio
        totalElderlyPopulation += elderlyPop * ratio
        totalLateElderlyPopulation += lateElderlyPop * ratio
        totalForeignPopulation += foreignPop * ratio
        totalHouseholdCount += householdCnt * ratio
        totalSingleHouseholdCount += singleHouseholdCnt * ratio
        totalElderlySingleHouseholdCount += elderlySingleCnt * ratio
        totalElderlyCoupleHouseholdCount += elderlyCoupleCnt * ratio
        // ピラミッド用男女別集計
        totalMale += male * ratio
        totalFemale += female * ratio
        totalYoungMale += youngMale * ratio
        totalYoungFemale += youngFemale * ratio
        totalWorkingMale += workingMale * ratio
        totalWorkingFemale += workingFemale * ratio
        totalElderly65Male += elderly65Male * ratio
        totalElderly65Female += elderly65Female * ratio
        totalElderly75Male += elderly75Male * ratio
        totalElderly75Female += elderly75Female * ratio
        totalElderly85Male += elderly85Male * ratio
        totalElderly85Female += elderly85Female * ratio
        totalElderly95Male += elderly95Male * ratio
        totalElderly95Female += elderly95Female * ratio
        const usedMeshFeature = {
          ...meshFeature,
          properties: {
            meshId: String(meshId),
            population,
            youngPop,
            workingAgePop,
            elderlyPop,
            lateElderlyPop,
            foreignPop,
            householdCnt,
            singleHouseholdCnt,
            elderlySingleCnt,
            elderlyCoupleCnt,
            ratio,
          },
        }
        usedMeshFeatures.push(usedMeshFeature)

        // デバッグ: 大きな寄与があるメッシュをログ
        if (population * ratio > 100) {
        }
      }
    } catch (e: any) {
      // 交差計算エラー（トポロジーエラーなど）は無視して続行
      console.warn('[AI防災リスク診断] 交差計算エラー', e.message)
    }
  }

  const endTime = performance.now()
  const elapsedMs = endTime - startTime


  // ピラミッドデータを計算（6区分）
  const pyramidData: PyramidAgeGroup[] = [
    { ageClass: '0-14歳', male: Math.round(totalYoungMale), female: Math.round(totalYoungFemale) },
    { ageClass: '15-64歳', male: Math.round(totalWorkingMale), female: Math.round(totalWorkingFemale) },
    { ageClass: '65-74歳', male: Math.round(totalElderly65Male - totalElderly75Male), female: Math.round(totalElderly65Female - totalElderly75Female) },
    { ageClass: '75-84歳', male: Math.round(totalElderly75Male - totalElderly85Male), female: Math.round(totalElderly75Female - totalElderly85Female) },
    { ageClass: '85-94歳', male: Math.round(totalElderly85Male - totalElderly95Male), female: Math.round(totalElderly85Female - totalElderly95Female) },
    { ageClass: '95歳以上', male: Math.round(totalElderly95Male), female: Math.round(totalElderly95Female) },
  ]

  return {
    analyzed: true,
    population: Math.round(totalPopulation),
    populationRaw: totalPopulation,
    youngPopulation: Math.round(totalYoungPopulation),
    workingAgePopulation: Math.round(totalWorkingAgePopulation),
    elderlyPopulation: Math.round(totalElderlyPopulation),
    lateElderlyPopulation: Math.round(totalLateElderlyPopulation),
    foreignPopulation: Math.round(totalForeignPopulation),
    householdCount: Math.round(totalHouseholdCount),
    singleHouseholdCount: Math.round(totalSingleHouseholdCount),
    elderlySingleHouseholdCount: Math.round(totalElderlySingleHouseholdCount),
    elderlyCoupleHouseholdCount: Math.round(totalElderlyCoupleHouseholdCount),
    maleTotal: Math.round(totalMale),
    femaleTotal: Math.round(totalFemale),
    pyramidData,
    areaKm2: polygonAreaKm2,
    populationDensity: polygonAreaKm2 > 0 ? Math.round(totalPopulation / polygonAreaKm2) : 0,
    meshCount: intersectedMeshCount,
    totalMeshCount: meshCount,
    meshFeatures: usedMeshFeatures,
    elapsedMs: elapsedMs,
  }
}

export async function analyzePopulation125mPoint(
  point: Feature<Point>,
  includeSyochiikiDetail: boolean = false
): Promise<PopulationResult> {

  const startTime = performance.now()
  const [lng, lat] = point.geometry.coordinates

  if (!pmtilesCache[MESH_125M_URL]) {
    pmtilesCache[MESH_125M_URL] = new PMTiles(MESH_125M_URL)
  }
  const pmtiles = pmtilesCache[MESH_125M_URL]

  let zoom = 15
  try {
    const header = await pmtiles.getHeader()
    zoom = header.maxZoom
  } catch (e) {
    console.warn('[AI防災リスク診断] ヘッダー取得エラー:', e)
  }

  const tile = lngLatToTile(lng, lat, zoom)
  let population = 0
  let youngPopulation = 0
  let workingAgePopulation = 0
  let elderlyPopulation = 0
  let lateElderlyPopulation = 0
  let foreignPopulation = 0
  let householdCount = 0
  let singleHouseholdCount = 0
  let elderlySingleHouseholdCount = 0
  let elderlyCoupleHouseholdCount = 0
  // ピラミッド用男女別データ
  let maleTotal = 0
  let femaleTotal = 0
  let youngMale = 0
  let youngFemale = 0
  let workingMale = 0
  let workingFemale = 0
  let elderly65Male = 0
  let elderly65Female = 0
  let elderly75Male = 0
  let elderly75Female = 0
  let elderly85Male = 0
  let elderly85Female = 0
  let elderly95Male = 0
  let elderly95Female = 0
  let meshId = ''
  let areaKm2 = 0
  let meshCount = 0
  let meshFeatures: Feature<Polygon>[] = []

  try {
    const tileData = await pmtiles.getZxy(tile.z, tile.x, tile.y)
    if (tileData?.data) {
      const vt = new VectorTile(new Pbf(tileData.data))
      const layer = vt.layers['polygon']
      if (layer) {
        const pt = turf.point([lng, lat])
        for (let i = 0; i < layer.length; i++) {
          const feature = layer.feature(i)
          const meshGeoJSON = featureToGeoJSON(feature, tile)
          if (turf.booleanPointInPolygon(pt, meshGeoJSON)) {
            const props: any = feature.properties || {}
            const toNum = (v: any) => (v === null || v === undefined || v === '' ? 0 : Number(v))
            population = toNum(props.T001225001)
            youngPopulation = toNum(props.T001225004)
            workingAgePopulation = toNum(props.T001225010)
            elderlyPopulation = toNum(props.T001225019)
            lateElderlyPopulation = toNum(props.T001225022)
            foreignPopulation = toNum(props.T001225031)
            householdCount = toNum(props.T001225034)
            singleHouseholdCount = toNum(props.T001225036)
            elderlySingleHouseholdCount = toNum(props.T001225049)
            elderlyCoupleHouseholdCount = toNum(props.T001225050)
            // ピラミッド用男女別データ
            maleTotal = toNum(props.T001225002)
            femaleTotal = toNum(props.T001225003)
            youngMale = toNum(props.T001225005)
            youngFemale = toNum(props.T001225006)
            workingMale = toNum(props.T001225011)
            workingFemale = toNum(props.T001225012)
            elderly65Male = toNum(props.T001225020)
            elderly65Female = toNum(props.T001225021)
            elderly75Male = toNum(props.T001225023)
            elderly75Female = toNum(props.T001225024)
            elderly85Male = toNum(props.T001225026)
            elderly85Female = toNum(props.T001225027)
            elderly95Male = toNum(props.T001225029)
            elderly95Female = toNum(props.T001225030)
            meshId = props.KEY_CODE ? String(props.KEY_CODE) : ''
            areaKm2 = turf.area(meshGeoJSON) / 1_000_000
            meshCount = 1
            meshFeatures = [{
              ...meshGeoJSON,
              properties: {
                meshId: props.KEY_CODE ? String(props.KEY_CODE) : '',
                population,
                youngPopulation,
                workingAgePopulation,
                elderlyPopulation,
                lateElderlyPopulation,
                foreignPopulation,
                householdCount,
                singleHouseholdCount,
                elderlySingleHouseholdCount,
                elderlyCoupleHouseholdCount,
                ratio: 1,
              },
            }]
            break
          }
        }
      }
    }
  } catch (e) {
    console.warn('[AI防災リスク診断] タイル取得エラー:', e)
  }

  // ピラミッドデータを計算（6区分）
  const pyramidData: PyramidAgeGroup[] = [
    { ageClass: '0-14歳', male: youngMale, female: youngFemale },
    { ageClass: '15-64歳', male: workingMale, female: workingFemale },
    { ageClass: '65-74歳', male: elderly65Male - elderly75Male, female: elderly65Female - elderly75Female },
    { ageClass: '75-84歳', male: elderly75Male - elderly85Male, female: elderly75Female - elderly85Female },
    { ageClass: '85-94歳', male: elderly85Male - elderly95Male, female: elderly85Female - elderly95Female },
    { ageClass: '95歳以上', male: elderly95Male, female: elderly95Female },
  ]

  // 超人口データは必要な場合のみ取得（AI人口プロファイルで使用）
  // 防災リスク診断では不要なのでスキップして高速化
  let syochiikiDetail: SyochiikiDetailData | undefined
  if (includeSyochiikiDetail) {
    try {
      const detail = await fetchSyochiikiDetailData(lng, lat)
      if (detail) {
        syochiikiDetail = detail
      }
    } catch (e) {
      console.warn('[AI人口プロファイル] 超人口データ取得エラー:', e)
    }
  }

  const elapsedMs = performance.now() - startTime

  return {
    analyzed: true,
    population,
    populationRaw: population,
    youngPopulation,
    workingAgePopulation,
    elderlyPopulation,
    lateElderlyPopulation,
    foreignPopulation,
    householdCount,
    singleHouseholdCount,
    elderlySingleHouseholdCount,
    elderlyCoupleHouseholdCount,
    maleTotal,
    femaleTotal,
    pyramidData,
    syochiikiDetail,
    meshId,
    meshFeatures,
    areaKm2,
    populationDensity: areaKm2 > 0 ? population / areaKm2 : 0,
    meshCount,
    totalMeshCount: meshCount,
    elapsedMs,
  }
}

// ============================================================
// ハザードマップ解析（全種類対応）
// ============================================================

interface HazardLegendItem {
  r: number
  g: number
  b: number
  title: string
  order: number
}

interface HazardLayerDef {
  id: string
  name: string
  url: string
  legend: HazardLegendItem[]
  zoom: number
  icon: string
  layerId: string // レイヤーツリーのID
  sampleStep?: { lat: number; lng: number }
}

// 洪水浸水想定の凡例
const LEGEND_FLOOD = [
  { r: 247, g: 245, b: 169, title: '0.5m未満', order: 1 },
  { r: 255, g: 216, b: 192, title: '0.5～3.0m', order: 2 },
  { r: 255, g: 183, b: 183, title: '3.0～5.0m', order: 3 },
  { r: 255, g: 145, b: 145, title: '5.0～10.0m', order: 4 },
  { r: 242, g: 133, b: 201, title: '10.0～20.0m', order: 5 },
  { r: 220, g: 122, b: 220, title: '20.0m以上', order: 6 },
]

// 津波浸水想定の凡例
const LEGEND_TSUNAMI = [
  { r: 255, g: 255, b: 179, title: '0.3m未満', order: 1 },
  { r: 247, g: 245, b: 169, title: '0.3～0.5m', order: 2 },
  { r: 248, g: 225, b: 166, title: '0.5～1.0m', order: 3 },
  { r: 255, g: 216, b: 192, title: '1.0～3.0m', order: 4 },
  { r: 255, g: 183, b: 183, title: '3.0～5.0m', order: 5 },
  { r: 255, g: 145, b: 145, title: '5.0～10.0m', order: 6 },
  { r: 242, g: 133, b: 201, title: '10.0～20.0m', order: 7 },
  { r: 220, g: 122, b: 188, title: '20.0m以上', order: 8 },
]

// 土砂災害警戒区域の凡例
const LEGEND_LANDSLIDE = [
  { r: 229, g: 200, b: 49, title: '土石流警戒区域', order: 1 },
  { r: 230, g: 201, b: 49, title: '土石流警戒区域', order: 1 },
  { r: 230, g: 200, b: 50, title: '土石流警戒区域', order: 1 },
  { r: 165, g: 0, b: 33, title: '土石流特別警戒区域', order: 2 },
  { r: 169, g: 10, b: 34, title: '土石流特別警戒区域', order: 2 },
  { r: 169, g: 10, b: 33, title: '土石流特別警戒区域', order: 2 },
]

// 土石流危険渓流の凡例
const LEGEND_DEBRIS = [
  { r: 245, g: 153, b: 101, title: '土石流危険渓流', order: 1 },
]

// 急傾斜地崩壊危険箇所の凡例
const LEGEND_STEEP = [
  { r: 224, g: 224, b: 254, title: '急傾斜地崩壊危険箇所', order: 1 },
]

// 地すべり危険箇所の凡例
const LEGEND_JISUBERI = [
  { r: 255, g: 235, b: 223, title: '地すべり危険箇所', order: 1 },
]

// ハザードレイヤー定義
export const HAZARD_LAYERS: HazardLayerDef[] = [
  {
    id: 'flood_max',
    name: '洪水浸水想定（想定最大規模）',
    url: 'https://disaportaldata.gsi.go.jp/raster/01_flood_l2_shinsuishin/{z}/{x}/{y}.png',
    legend: LEGEND_FLOOD,
    zoom: 16,
    icon: 'mdi-waves',
    layerId: 'oh-kozui-saidai',
  },
  // {
  //   id: 'flood_planned',
  //   name: '洪水浸水想定（計画規模）',
  //   url: 'https://disaportaldata.gsi.go.jp/raster/01_flood_l1_shinsuishin_newlegend_kuni_data/{z}/{x}/{y}.png',
  //   legend: LEGEND_FLOOD,
  //   zoom: 16,
  //   icon: 'mdi-waves',
  //   layerId: 'oh-kozui-keikaku',
  // },
  {
    id: 'tsunami',
    name: '津波浸水想定',
    url: 'https://disaportaldata.gsi.go.jp/raster/04_tsunami_newlegend_data/{z}/{x}/{y}.png',
    legend: LEGEND_TSUNAMI,
    zoom: 16,
    icon: 'mdi-wave',
    layerId: 'oh-tsunami',
  },
  {
    id: 'landslide_alert',
    name: '土砂災害警戒区域',
    url: 'https://disaportaldata.gsi.go.jp/raster/05_dosekiryukeikaikuiki/{z}/{x}/{y}.png',
    legend: LEGEND_LANDSLIDE,
    zoom: 16,
    icon: 'mdi-image-filter-hdr',
    layerId: 'oh-dosya',
  },
  {
    id: 'debris_flow',
    name: '土石流危険渓流',
    url: 'https://disaportaldata.gsi.go.jp/raster/05_dosekiryukikenkeiryu/{z}/{x}/{y}.png',
    legend: LEGEND_DEBRIS,
    zoom: 16,
    icon: 'mdi-image-filter-hdr',
    layerId: 'oh-dosekiryu',
    sampleStep: { lat: 0.00015, lng: 0.0002 },
  },
  {
    id: 'steep_slope',
    name: '急傾斜地崩壊危険箇所',
    url: 'https://disaportaldata.gsi.go.jp/raster/05_kyukeisyachihoukai/{z}/{x}/{y}.png',
    legend: LEGEND_STEEP,
    zoom: 16,
    icon: 'mdi-image-filter-hdr',
    layerId: 'oh-kyukeisya',
    sampleStep: { lat: 0.00015, lng: 0.0002 },
  },
  {
    id: 'landslide_risk',
    name: '地すべり危険箇所',
    url: 'https://disaportaldata.gsi.go.jp/raster/05_jisuberikikenkasyo/{z}/{x}/{y}.png',
    legend: LEGEND_JISUBERI,
    zoom: 16,
    icon: 'mdi-image-filter-hdr',
    layerId: 'oh-jisuberi',
    sampleStep: { lat: 0.00015, lng: 0.0002 },
  },
]

/**
 * 緯度経度からタイル内のピクセル位置を計算
 */
function lngLatToTilePixel(lng: number, lat: number, zoom: number): { tileX: number; tileY: number; pixelX: number; pixelY: number } {
  const n = Math.pow(2, zoom)
  const tileXFloat = ((lng + 180) / 360) * n
  const latRad = (lat * Math.PI) / 180
  const tileYFloat = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n

  const tileX = Math.floor(tileXFloat)
  const tileY = Math.floor(tileYFloat)
  const pixelX = Math.floor((tileXFloat - tileX) * 256)
  const pixelY = Math.floor((tileYFloat - tileY) * 256)

  return { tileX, tileY, pixelX, pixelY }
}

/**
 * タイル画像を取得してImageDataを返す
 */
async function fetchTileImageData(url: string, tileX: number, tileY: number, zoom: number): Promise<ImageData | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 256
      canvas.height = 256
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve(ctx.getImageData(0, 0, 256, 256))
    }

    img.onerror = () => {
      resolve(null)
    }

    const tileUrl = url.replace('{z}', String(zoom)).replace('{x}', String(tileX)).replace('{y}', String(tileY))
    img.src = tileUrl
  })
}

/**
 * RGBから凡例を判定
 */
function matchLegend(r: number, g: number, b: number, a: number, legend: HazardLegendItem[]): HazardLegendItem | null {
  if (a < 128) return null // 透明ピクセルは無視

  for (const item of legend) {
    // 色の許容誤差（±10）
    if (
      Math.abs(r - item.r) <= 10 &&
      Math.abs(g - item.g) <= 10 &&
      Math.abs(b - item.b) <= 10
    ) {
      return item
    }
  }
  return null
}

/**
 * サンプルポイントを生成
 */
function generateSamplePoints(polygon: Feature<Polygon>): [number, number][] {
  return generateSamplePointsWithStep(polygon, 0.00045, 0.0006)
}

function generateSamplePointsWithStep(polygon: Feature<Polygon>, latStep: number, lngStep: number): [number, number][] {
  const bbox = turf.bbox(polygon)
  const [minLng, minLat, maxLng, maxLat] = bbox

  const samplePoints: [number, number][] = []
  for (let lat = minLat; lat <= maxLat; lat += latStep) {
    for (let lng = minLng; lng <= maxLng; lng += lngStep) {
      const pt = turf.point([lng, lat])
      if (turf.booleanPointInPolygon(pt, polygon)) {
        samplePoints.push([lng, lat])
      }
    }
  }
  return samplePoints
}

/**
 * 単一ハザードレイヤーを解析
 */
async function analyzeHazardLayer(
  layerDef: HazardLayerDef,
  samplePoints: [number, number][]
): Promise<HazardResult> {
  const startTime = performance.now()

  if (samplePoints.length === 0) {
    return {
      analyzed: true,
      hasRisk: false,
      maxValue: null,
      distribution: [],
      totalSamples: 0,
      affectedSamples: 0,
      elapsedMs: 0,
    }
  }

  // タイルごとにグループ化
  const tileGroups: Map<string, { tileX: number; tileY: number; points: { pixelX: number; pixelY: number }[] }> = new Map()

  for (const [lng, lat] of samplePoints) {
    const { tileX, tileY, pixelX, pixelY } = lngLatToTilePixel(lng, lat, layerDef.zoom)
    const key = `${tileX}_${tileY}`

    if (!tileGroups.has(key)) {
      tileGroups.set(key, { tileX, tileY, points: [] })
    }
    tileGroups.get(key)!.points.push({ pixelX, pixelY })
  }

  // 値の集計
  const valueCounts: Map<string, { count: number; item: HazardLegendItem }> = new Map()
  let affectedSamples = 0

  // タイルを取得して解析
  const tilePromises = Array.from(tileGroups.values()).map(async (group) => {
    const imageData = await fetchTileImageData(layerDef.url, group.tileX, group.tileY, layerDef.zoom)
    if (!imageData) return

    for (const { pixelX, pixelY } of group.points) {
      const idx = (pixelY * 256 + pixelX) * 4
      const r = imageData.data[idx]
      const g = imageData.data[idx + 1]
      const b = imageData.data[idx + 2]
      const a = imageData.data[idx + 3]

      const matched = matchLegend(r, g, b, a, layerDef.legend)
      if (matched) {
        const existing = valueCounts.get(matched.title)
        if (existing) {
          existing.count++
        } else {
          valueCounts.set(matched.title, { count: 1, item: matched })
        }
        affectedSamples++
      }
    }
  })

  await Promise.all(tilePromises)

  // 結果を整形（orderでソート）
  const distribution = Array.from(valueCounts.entries())
    .map(([value, { count, item }]) => ({
      value,
      areaRatio: count / samplePoints.length,
      sampleCount: count,
      color: `rgb(${item.r}, ${item.g}, ${item.b})`,
      order: item.order,
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ value, areaRatio, sampleCount, color }) => ({ value, areaRatio, sampleCount, color }))

  // 最大値を取得（orderが最大のもの）
  let maxValue: string | null = null
  let maxOrder = 0
  for (const [value, { item }] of valueCounts.entries()) {
    if (item.order > maxOrder) {
      maxOrder = item.order
      maxValue = value
    }
  }

  const elapsedMs = performance.now() - startTime

  return {
    analyzed: true,
    hasRisk: affectedSamples > 0,
    maxValue,
    distribution,
    totalSamples: samplePoints.length,
    affectedSamples,
    elapsedMs,
  }
}

/**
 * 全ハザードを解析
 */
async function analyzeAllHazards(polygon: Feature<Polygon>): Promise<Record<string, HazardResult>> {

  // サンプルポイントを一度だけ生成（全ハザードで共有）
  const samplePoints = generateSamplePoints(polygon)

  // 全ハザードを並列解析
  const results: Record<string, HazardResult> = {}
  const samplePointCache = new Map<string, [number, number][]>()
  const promises = HAZARD_LAYERS.map(async (layerDef) => {
    let pointsForLayer = samplePoints
    if (layerDef.sampleStep) {
      const key = `${layerDef.sampleStep.lat}-${layerDef.sampleStep.lng}`
      if (!samplePointCache.has(key)) {
        samplePointCache.set(
          key,
          generateSamplePointsWithStep(polygon, layerDef.sampleStep.lat, layerDef.sampleStep.lng)
        )
      }
      pointsForLayer = samplePointCache.get(key) || samplePoints
    }
    const result = await analyzeHazardLayer(layerDef, pointsForLayer)
    results[layerDef.id] = result
    if (result.hasRisk) {
    }
  })

  await Promise.all(promises)

  return results
}

async function analyzeAllHazardsPoint(point: Feature<Point>): Promise<Record<string, HazardResult>> {

  const [lng, lat] = point.geometry.coordinates
  const samplePoints: [number, number][] = [[lng, lat]]

  const results: Record<string, HazardResult> = {}
  const promises = HAZARD_LAYERS.map(async (layerDef) => {
    const result = await analyzeHazardLayer(layerDef, samplePoints)
    results[layerDef.id] = result
  })

  await Promise.all(promises)
  return results
}

/**
 * 不動産情報ライブラリ（ハザード系のみ）を解析
 */
async function analyzeReinfolibHazards(polygon: Feature<Polygon>): Promise<ReinfolibHazardSummary> {
  try {
    const areaKm2 = turf.area(polygon) / 1_000_000
    const maxPoints = pickReinfolibSampleCount(areaKm2)
    const candidatePoints = buildReinfolibSamplePoints(polygon)
    const points = dedupePointsByTile(candidatePoints, maxPoints)

    const responses = await Promise.all(
      points.map(async ([lng, lat]) => (await fetchReinfolibAll(lng, lat)) as ReinfolibBatchResult)
    )

    const validResponses = responses.filter(r => r && r.success)
    if (validResponses.length === 0) {
      return {
        analyzed: false,
        hitCount: 0,
        apiCount: REINFOLIB_HAZARD_APIS.length,
        hits: [],
        misses: REINFOLIB_HAZARD_APIS.map(api => ({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
        })),
        error: responses.find(r => r?.error)?.error || '不動産情報ライブラリの取得に失敗しました',
      }
    }

    const results: ReinfolibApiResult[] = []
    for (const res of validResponses) {
      if (Array.isArray(res.results)) {
        results.push(...res.results)
      }
    }
    const hits: ReinfolibHazardHit[] = []
    const misses: { api: string; name: string; layerId: string | null }[] = []

    for (const api of REINFOLIB_HAZARD_APIS) {
      const candidates = results.filter((r: ReinfolibApiResult) => r.api === api.api)
      const result = candidates.reduce<ReinfolibApiResult | null>((best, current) => {
        if (!best) return current
        if (best.distance === undefined) return best
        if (current.distance === undefined) return best
        return current.distance < best.distance ? current : best
      }, null)
      if (result) {
        hits.push({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
          detail: formatSingleResultForAi(result),
        })
      } else {
        misses.push({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
        })
      }
    }

    const evacuationStats = await getEvacuationStatsFromTiles(polygon)
    const evacuationSummary = summarizeEvacuation(results, polygon, evacuationStats.count)
    evacuationSummary.nearestName = evacuationStats.nearestName
    evacuationSummary.nearestDistance = evacuationStats.nearestDistance
    evacuationSummary.nearestCoords = evacuationStats.nearestCoords
    evacuationSummary.nearestList = evacuationStats.nearestList
    return {
      analyzed: true,
      hitCount: hits.length,
      apiCount: REINFOLIB_HAZARD_APIS.length,
      hits,
      misses,
      evacuation: evacuationSummary,
    }
  } catch (error: any) {
    console.warn('[reinfolib] ハザード取得エラー', error)
    return {
      analyzed: false,
      hitCount: 0,
      apiCount: REINFOLIB_HAZARD_APIS.length,
      hits: [],
      misses: REINFOLIB_HAZARD_APIS.map(api => ({
        api: api.api,
        name: api.name,
        layerId: api.layerId,
      })),
      evacuation: summarizeEvacuation([], polygon),
      error: error?.message || '不動産情報ライブラリの取得に失敗しました',
    }
  }
}

async function analyzeReinfolibHazardsPoint(
  point: Feature<Point>,
  reinfolibData?: ReinfolibBatchResult
): Promise<ReinfolibHazardSummary> {
  try {
    const [lng, lat] = point.geometry.coordinates
    const reinfolibPayload = reinfolibData || ((await fetchReinfolibAll(lng, lat)) as ReinfolibBatchResult)

    if (!reinfolibPayload || !reinfolibPayload.success) {
      return {
        analyzed: false,
        hitCount: 0,
        apiCount: REINFOLIB_HAZARD_APIS.length,
        hits: [],
        misses: REINFOLIB_HAZARD_APIS.map(api => ({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
        })),
        error: reinfolibPayload?.error || '不動産情報ライブラリの取得に失敗しました',
      }
    }

    const results = Array.isArray(reinfolibPayload.results) ? reinfolibPayload.results : []
    const hits: ReinfolibHazardHit[] = []
    const misses: { api: string; name: string; layerId: string | null }[] = []

    for (const api of REINFOLIB_HAZARD_APIS) {
      const result = results.find((r: ReinfolibApiResult) => r.api === api.api)
      if (result) {
        hits.push({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
          detail: formatSingleResultForAi(result),
        })
      } else {
        misses.push({
          api: api.api,
          name: api.name,
          layerId: api.layerId,
        })
      }
    }

    return {
      analyzed: true,
      hitCount: hits.length,
      apiCount: REINFOLIB_HAZARD_APIS.length,
      hits,
      misses,
      evacuation: await (async () => {
        const summary = summarizeEvacuation(results)
        const list = await computeNearestEvacuationsForPoint(point, results, 3)
        summary.nearestList = list
        const nearest = list[0]
        if (nearest) {
          summary.nearestName = nearest.name
          summary.nearestDistance = nearest.distance
          summary.nearestCoords = nearest.coords || null
          if (nearest.coords) {
            const start: [number, number] = [
              point.geometry.coordinates[0],
              point.geometry.coordinates[1],
            ]
            const end: [number, number] = [nearest.coords[0], nearest.coords[1]]
            const [walkingMinutes, drivingMinutes] = await Promise.all([
              fetchRouteDurationMinutes(start, end, 'foot-hiking'),
              fetchRouteDurationMinutes(start, end, 'driving-car'),
            ])
            summary.walkingMinutes = walkingMinutes
            summary.drivingMinutes = drivingMinutes
          }
        }
        return summary
      })(),
    }
  } catch (error: any) {
    console.warn('[reinfolib] ハザード取得エラー', error)
    return {
      analyzed: false,
      hitCount: 0,
      apiCount: REINFOLIB_HAZARD_APIS.length,
      hits: [],
      misses: REINFOLIB_HAZARD_APIS.map(api => ({
        api: api.api,
        name: api.name,
        layerId: api.layerId,
      })),
      evacuation: summarizeEvacuation([]),
      error: error?.message || '不動産情報ライブラリの取得に失敗しました',
    }
  }
}

/**
 * AI防災リスク診断のメイン関数
 */
export async function analyzeArea(feature: Feature<Polygon | Point>): Promise<AreaAnalysisResult> {

  if (!feature || !feature.geometry) {
    console.error('[AI防災リスク診断] 無効なジオメトリ')
    return {
      analyzed: false,
      error: '無効なジオメトリ',
    }
  }

  if (feature.geometry.type === 'Point') {
    const point = feature as Feature<Point>
    const [populationResult, hazardsResult, elevation, address, reinfolibPayload, geology] = await Promise.all([
      analyzePopulation125mPoint(point),
      analyzeAllHazardsPoint(point),
      fetchElevation(point.geometry.coordinates[0], point.geometry.coordinates[1]),
      fetchAddress(point.geometry.coordinates[0], point.geometry.coordinates[1]),
      fetchReinfolibAll(point.geometry.coordinates[0], point.geometry.coordinates[1]),
      fetchGeology(point.geometry.coordinates[0], point.geometry.coordinates[1]),
    ])
    const reinfolibHazards = await analyzeReinfolibHazardsPoint(point, reinfolibPayload as ReinfolibBatchResult)
    const results = Array.isArray((reinfolibPayload as ReinfolibBatchResult)?.results)
      ? (reinfolibPayload as ReinfolibBatchResult).results as ReinfolibApiResult[]
      : []
    const medical = extractNearestReinfolibMedical(results)

    return {
      analyzed: true,
      population: populationResult,
      hazards: hazardsResult,
      reinfolibHazards,
      elevation,
      medical: {
        name: medical.name,
        distance: medical.distance,
        layerId: 'oh-reinfolib-iryo',
      },
      address,
      geology,
    }
  }

  if (feature.geometry.type !== 'Polygon') {
    console.error('[AI防災リスク診断] 対応外のジオメトリ')
    return {
      analyzed: false,
      error: '対応外のジオメトリ',
    }
  }

  const polygon = feature as Feature<Polygon>

  // 面積制限チェック（800,000 m²を上限）
  const MAX_AREA = 800000 // m²
  const area = turf.area(polygon)
  if (area > MAX_AREA) {
    const areaMan = (area / 10000).toFixed(1)
    console.warn(`[AI防災リスク診断] 面積超過: ${areaMan}万m²（上限: 80万m²）`)
    return {
      analyzed: false,
      error: `範囲が大きすぎます（${areaMan}万m²）。80万m²以下の範囲を選択してください。`,
    }
  }
  const centroid = turf.centroid(polygon)
  const [populationResult, hazardsResult, reinfolibHazards, elevation, address, geology] = await Promise.all([
    analyzePopulation125m(polygon),
    analyzeAllHazards(polygon),
    analyzeReinfolibHazards(polygon),
    fetchElevation(centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]),
    fetchAddress(centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]),
    fetchGeology(centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]),
  ])

  return {
    analyzed: true,
    population: populationResult,
    hazards: hazardsResult,
    reinfolibHazards,
    elevation,
    address,
    geology,
  }
}

// ============================================================
// AI防災リスク診断専用フリーハンド描画
// ドローとは完全に独立
// ============================================================

const PREVIEW_SOURCE_ID = 'area-analysis-preview-source'
const PREVIEW_LAYER_ID = 'area-analysis-preview-layer'

/**
 * プレビュー用のソースとレイヤーを初期化（circle描画・polygon描画で使用）
 */
function initPreviewLayer(map: any): void {
  if (!map.getSource(PREVIEW_SOURCE_ID)) {
    map.addSource(PREVIEW_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getLayer(PREVIEW_LAYER_ID)) {
    map.addLayer({
      id: PREVIEW_LAYER_ID,
      type: 'line',
      source: PREVIEW_SOURCE_ID,
      paint: {
        'line-color': '#2196F3',
        'line-width': 3,
        'line-dasharray': [2, 2],
      },
    })
  }
}

/**
 * プレビューをクリア（circle描画・polygon描画で使用）
 */
function clearPreview(map: any): void {
  const source = map.getSource(PREVIEW_SOURCE_ID)
  if (source) {
    source.setData({ type: 'FeatureCollection', features: [] })
  }
}

// ============================================================
// フリーハンド描画（Canvas overlay方式・高速版）
// ============================================================

interface FreehandState {
  active: boolean
  coords: [number, number][]      // 地理座標（最終変換用）
  screenCoords: [number, number][] // スクリーン座標（Canvas描画用）
  map: any
  mapCanvas: HTMLCanvasElement | null   // MapLibreのcanvas（イベント取得用）
  overlayCanvas: HTMLCanvasElement | null  // 描画用Canvasオーバーレイ
  overlayCtx: CanvasRenderingContext2D | null
  handlers: {
    pointerdown: ((e: PointerEvent) => void) | null
    pointermove: ((e: PointerEvent) => void) | null
    pointerup: ((e: PointerEvent) => void) | null
  }
  resolve: ((polygon: Feature<Polygon> | null) => void) | null
}

const freehandState: FreehandState = {
  active: false,
  coords: [],
  screenCoords: [],
  map: null,
  mapCanvas: null,
  overlayCanvas: null,
  overlayCtx: null,
  handlers: {
    pointerdown: null,
    pointermove: null,
    pointerup: null,
  },
  resolve: null,
}

/**
 * Canvas overlayを作成して地図の上に重ねる（描画プレビュー用）
 * GeoJSON source.setData()を毎フレーム呼ぶ旧方式より圧倒的に高速
 */
function createOverlayCanvas(map: any): { canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D } {
  const mapCanvas = map.getCanvas()
  const container = map.getContainer()

  const overlay = document.createElement('canvas')
  overlay.width = mapCanvas.width
  overlay.height = mapCanvas.height
  overlay.style.position = 'absolute'
  overlay.style.top = '0'
  overlay.style.left = '0'
  overlay.style.width = mapCanvas.style.width || mapCanvas.clientWidth + 'px'
  overlay.style.height = mapCanvas.style.height || mapCanvas.clientHeight + 'px'
  overlay.style.pointerEvents = 'none'  // クリックは下のMapLibre canvasに透過
  overlay.style.zIndex = '10'

  container.appendChild(overlay)

  const ctx = overlay.getContext('2d')!
  // Retinaディスプレイ対応: devicePixelRatioでスケーリング
  const dpr = window.devicePixelRatio || 1
  overlay.width = mapCanvas.clientWidth * dpr
  overlay.height = mapCanvas.clientHeight * dpr
  overlay.style.width = mapCanvas.clientWidth + 'px'
  overlay.style.height = mapCanvas.clientHeight + 'px'
  ctx.scale(dpr, dpr)

  return { canvas: overlay, ctx }
}

/**
 * Canvas overlayを削除
 */
function removeOverlayCanvas(): void {
  if (freehandState.overlayCanvas && freehandState.overlayCanvas.parentNode) {
    freehandState.overlayCanvas.parentNode.removeChild(freehandState.overlayCanvas)
  }
  freehandState.overlayCanvas = null
  freehandState.overlayCtx = null
}

/**
 * Canvas上にフリーハンドの線を増分描画（前回の最後の点から新しい点へ1セグメントだけ）
 */
function drawSegment(ctx: CanvasRenderingContext2D, from: [number, number], to: [number, number]): void {
  ctx.beginPath()
  ctx.moveTo(from[0], from[1])
  ctx.lineTo(to[0], to[1])
  ctx.stroke()
}

/**
 * ループ検出（Turf.jsで自己交差を検出）
 * 座標が多いと重いので、呼び出し頻度を制限して使うこと
 */
function detectLoop(coords: [number, number][]): [number, number][] | null {
  if (coords.length < 10) return null

  const line = turf.lineString(coords)
  const kinks = turf.kinks(line)

  if (kinks.features.length > 0) {
    const intersection = kinks.features[0].geometry.coordinates as [number, number]
    const intersectionPoint = turf.point(intersection)

    let closestIdx = 0
    let minDist = Infinity
    for (let i = 0; i < coords.length - 5; i++) {
      const dist = turf.distance(turf.point(coords[i]), intersectionPoint)
      if (dist < minDist) {
        minDist = dist
        closestIdx = i
      }
    }

    const ring = [...coords.slice(closestIdx), coords[closestIdx]]
    return ring
  }

  return null
}

/**
 * フリーハンド描画を開始（Canvas overlay方式・高速版）
 * 描画中はHTML Canvas 2Dで直接線を引く。GeoJSON変換は描画完了時のみ。
 * @returns Promise<Feature<Polygon> | null> - 描画されたポリゴン、またはキャンセル時はnull
 */
export function startAreaAnalysisFreehand(map: any): Promise<Feature<Polygon> | null> {
  return new Promise((resolve) => {
    if (freehandState.active) {
      stopAreaAnalysisFreehand()
    }

    freehandState.active = true
    freehandState.coords = []
    freehandState.screenCoords = []
    freehandState.map = map
    freehandState.mapCanvas = map.getCanvas()
    freehandState.resolve = resolve

    // Canvas overlayを作成
    const { canvas: overlayCanvas, ctx } = createOverlayCanvas(map)
    freehandState.overlayCanvas = overlayCanvas
    freehandState.overlayCtx = ctx

    // 描画スタイル設定
    ctx.strokeStyle = '#2196F3'
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.setLineDash([8, 4])

    let isDrawing = false
    // ループ検出の頻度制限（N点ごとにチェック）
    let lastLoopCheckCount = 0
    const LOOP_CHECK_INTERVAL = 15

    const mapContainer = map.getContainer()
    const mapRect = mapContainer.getBoundingClientRect()

    const onPointerDown = (e: PointerEvent) => {
      if (!freehandState.active) return

      isDrawing = true
      freehandState.coords = []
      freehandState.screenCoords = []
      lastLoopCheckCount = 0

      // スクリーン座標（overlayCanvas上の座標に変換）
      const sx = e.clientX - mapRect.left
      const sy = e.clientY - mapRect.top
      freehandState.screenCoords.push([sx, sy])

      // 地理座標
      const point = map.unproject([e.clientX, e.clientY])
      freehandState.coords.push([point.lng, point.lat])

      map.getCanvas().classList.add('is-area-analysis-freehand')
      map.dragPan.disable()
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!isDrawing || !freehandState.active) return

      const sx = e.clientX - mapRect.left
      const sy = e.clientY - mapRect.top
      const point = map.unproject([e.clientX, e.clientY])
      const newCoord: [number, number] = [point.lng, point.lat]

      // 重複チェック（スクリーン座標で2px未満の移動は無視）
      const lastScreen = freehandState.screenCoords[freehandState.screenCoords.length - 1]
      if (lastScreen) {
        const dx = sx - lastScreen[0]
        const dy = sy - lastScreen[1]
        if (dx * dx + dy * dy < 4) return  // 2px未満
      }

      // Canvas上に線を引く（超高速・DOM更新なし）
      if (lastScreen && freehandState.overlayCtx) {
        drawSegment(freehandState.overlayCtx, lastScreen, [sx, sy])
      }

      freehandState.screenCoords.push([sx, sy])
      freehandState.coords.push(newCoord)

      // ループ検出（N点ごとにのみチェック → 軽量化）
      if (freehandState.coords.length - lastLoopCheckCount >= LOOP_CHECK_INTERVAL) {
        lastLoopCheckCount = freehandState.coords.length
        const ring = detectLoop(freehandState.coords)
        if (ring && ring.length >= 4) {
          isDrawing = false
          map.getCanvas().classList.remove('is-area-analysis-freehand')
          map.dragPan.enable()
          removeOverlayCanvas()

          const polygon: Feature<Polygon> = {
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [ring] },
            properties: {},
          }

          stopAreaAnalysisFreehand(true)
          resolve(polygon)
        }
      }
    }

    const onPointerUp = () => {
      if (!isDrawing || !freehandState.active) return

      isDrawing = false
      map.getCanvas().classList.remove('is-area-analysis-freehand')
      map.dragPan.enable()
      removeOverlayCanvas()

      // ポリゴンとして閉じる（最低3点必要）
      if (freehandState.coords.length >= 3) {
        const ring = [...freehandState.coords, freehandState.coords[0]]

        const polygon: Feature<Polygon> = {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
          properties: {},
        }

        stopAreaAnalysisFreehand(true)
        resolve(polygon)
      } else {
        stopAreaAnalysisFreehand(true)
        resolve(null)
      }
    }

    freehandState.handlers.pointerdown = onPointerDown
    freehandState.handlers.pointermove = onPointerMove
    freehandState.handlers.pointerup = onPointerUp

    freehandState.mapCanvas?.addEventListener('pointerdown', onPointerDown)
    freehandState.mapCanvas?.addEventListener('pointermove', onPointerMove)
    freehandState.mapCanvas?.addEventListener('pointerup', onPointerUp)

    map.getCanvas().classList.add('is-area-analysis-freehand')
  })
}

/**
 * フリーハンド描画を停止
 * @param skipResolve trueの場合はresolve(null)を呼ばない（正常完了時用）
 */
export function stopAreaAnalysisFreehand(skipResolve = false): void {
  if (!freehandState.active) return

  const { map, mapCanvas, handlers } = freehandState

  if (mapCanvas && handlers.pointerdown) {
    mapCanvas.removeEventListener('pointerdown', handlers.pointerdown)
  }
  if (mapCanvas && handlers.pointermove) {
    mapCanvas.removeEventListener('pointermove', handlers.pointermove)
  }
  if (mapCanvas && handlers.pointerup) {
    mapCanvas.removeEventListener('pointerup', handlers.pointerup)
  }

  removeOverlayCanvas()

  if (map) {
    map.getCanvas().classList.remove('is-area-analysis-freehand')
    map.dragPan.enable()
  }

  if (!skipResolve && freehandState.resolve) {
    freehandState.resolve(null)
  }

  freehandState.active = false
  freehandState.coords = []
  freehandState.screenCoords = []
  freehandState.map = null
  freehandState.mapCanvas = null
  freehandState.overlayCanvas = null
  freehandState.overlayCtx = null
  freehandState.handlers = { pointerdown: null, pointermove: null, pointerup: null }
  freehandState.resolve = null
}

/**
 * フリーハンドがアクティブかどうか
 */
export function isAreaAnalysisFreehandActive(): boolean {
  return freehandState.active
}

// ============================================================
// 分析ポリゴン表示
// ============================================================

const POLYGON_SOURCE_ID = 'area-analysis-polygon-source'
const POLYGON_FILL_LAYER_ID = 'area-analysis-polygon-fill'
const POLYGON_LINE_LAYER_ID = 'area-analysis-polygon-line'
const POLYGON_LABEL_LAYER_ID = 'area-analysis-polygon-label'

// 表示中のポリゴン管理
const displayedPolygons: Map<string, Feature<Polygon>> = new Map()
// ポリゴンの人口（別管理で確実に保持）
const polygonPopulations: Map<string, number> = new Map()

/**
 * ポリゴン表示用のソースとレイヤーを初期化
 */
function initPolygonLayers(map: any): void {
  if (!map.getSource(POLYGON_SOURCE_ID)) {
    map.addSource(POLYGON_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }

  if (!map.getLayer(POLYGON_FILL_LAYER_ID)) {
    map.addLayer({
      id: POLYGON_FILL_LAYER_ID,
      type: 'fill',
      source: POLYGON_SOURCE_ID,
      paint: {
        // データ駆動型: fillColor / fillOpacity があればそれを使用（デフォルト紫）
        'fill-color': ['coalesce', ['get', 'fillColor'], '#9C27B0'],
        'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.25],
      },
    })
  }

  if (!map.getLayer(POLYGON_LINE_LAYER_ID)) {
    map.addLayer({
      id: POLYGON_LINE_LAYER_ID,
      type: 'line',
      source: POLYGON_SOURCE_ID,
      paint: {
        // データ駆動型: lineColorプロパティがあればそれを使用（デフォルト紫）
        'line-color': ['coalesce', ['get', 'lineColor'], '#7B1FA2'],
        'line-width': 2.5,
      },
    })
  }

  // ラベルレイヤーを常に再作成（キャッシュ対策）
  if (map.getLayer(POLYGON_LABEL_LAYER_ID)) {
    map.removeLayer(POLYGON_LABEL_LAYER_ID)
  }
  map.addLayer({
    id: POLYGON_LABEL_LAYER_ID,
    type: 'symbol',
    source: POLYGON_SOURCE_ID,
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 14,
      'text-font': ['Noto Sans CJK JP Bold'],
      'text-anchor': 'center',
      'text-line-height': 1.3,
    },
    paint: {
      // データ駆動型: ラベル色もlineColorに合わせる（デフォルト紫）
      'text-color': ['coalesce', ['get', 'lineColor'], '#7B1FA2'],
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    },
  })
}

/**
 * 全ポリゴンを再描画
 */
function updatePolygonSource(map: any): void {
  const source = map.getSource(POLYGON_SOURCE_ID)
  if (!source) return

  const features = Array.from(displayedPolygons.values()).map((polygon) => {
    // ポリゴンの重心を計算してラベル位置に使用
    const centroid = turf.centroid(polygon)
    const baseLabel = polygon.properties?.label || ''
    const id = polygon.properties?.id as string
    // 人口は別Mapから取得（確実に保持）
    const population = id ? polygonPopulations.get(id) : undefined
    // ラベル = ナンバー + 人口（あれば）
    const label = population
      ? `${baseLabel}\n${population.toLocaleString()}人`
      : baseLabel

    return {
      ...polygon,
      properties: {
        ...polygon.properties,
        label,
        centroid: centroid.geometry.coordinates,
      },
    }
  })

  source.setData({
    type: 'FeatureCollection',
    features,
  })
}

/**
 * 分析ポリゴンを地図上に表示
 * @param map MapLibre map instance
 * @param id 一意のID（FWのinstanceId）
 * @param polygon 表示するポリゴン
 * @param label ラベル（ナンバリング）
 */
export function showAnalysisPolygon(
  map: any,
  id: string,
  polygon: Feature<Polygon>,
  label: string
): void {
  initPolygonLayers(map)

  // ラベルをプロパティに追加
  const polygonWithLabel = {
    ...polygon,
    properties: {
      ...polygon.properties,
      id,
      label,
    },
  }

  displayedPolygons.set(id, polygonWithLabel as Feature<Polygon>)
  updatePolygonSource(map)
}

/**
 * 分析ポリゴンを地図から削除
 * @param map MapLibre map instance
 * @param id 削除するポリゴンのID
 */
export function hideAnalysisPolygon(map: any, id: string): void {
  displayedPolygons.delete(id)
  polygonPopulations.delete(id)  // 人口Mapからも削除

  if (map && map.getSource(POLYGON_SOURCE_ID)) {
    updatePolygonSource(map)
  }
}

/**
 * 現在表示中の分析ポリゴンのIDセットを取得
 * shareFeatures との重複表示を防ぐために使用
 */
export function getActiveAnalysisPolygonIds(): Set<string> {
  return new Set(displayedPolygons.keys())
}

/**
 * リスクレベルに応じた色を取得
 * @param riskLevel 0: 安全, 1: 低リスク, 2: 中リスク, 3: 高リスク
 */
function getRiskColors(riskLevel: number): { fillColor: string; lineColor: string } {
  switch (riskLevel) {
    case 0: // 安全（緑）
      return { fillColor: '#4CAF50', lineColor: '#2E7D32' }
    case 1: // 低リスク（黄）
      return { fillColor: '#FFC107', lineColor: '#F57F17' }
    case 2: // 中リスク（オレンジ）
      return { fillColor: '#FF9800', lineColor: '#E65100' }
    case 3: // 高リスク（赤）
      return { fillColor: '#F44336', lineColor: '#C62828' }
    default: // デフォルト（青）
      return { fillColor: '#2196F3', lineColor: '#1976D2' }
  }
}

/**
 * ハザード結果からリスクレベルを計算
 */
export function calculateRiskLevel(hazards: Record<string, HazardResult> | undefined): number {
  if (!hazards) return 0

  let maxRisk = 0

  for (const hazardId of Object.keys(hazards)) {
    const result = hazards[hazardId]
    if (!result?.hasRisk) continue

    // ハザードの種類とmaxValueからリスクレベルを判定
    const maxValue = result.maxValue || ''

    // 洪水・津波: 浸水深でリスク判定
    if (hazardId === 'flood_max' || hazardId === 'tsunami') {
      if (maxValue.includes('10.0') || maxValue.includes('20.0')) {
        maxRisk = Math.max(maxRisk, 3) // 10m以上は高リスク
      } else if (maxValue.includes('5.0') || maxValue.includes('3.0')) {
        maxRisk = Math.max(maxRisk, 2) // 3-10mは中リスク
      } else {
        maxRisk = Math.max(maxRisk, 1) // それ以下は低リスク
      }
    }
    // 土砂災害警戒区域
    else if (hazardId === 'landslide_alert') {
      if (maxValue.includes('特別警戒')) {
        maxRisk = Math.max(maxRisk, 3) // 特別警戒区域は高リスク
      } else {
        maxRisk = Math.max(maxRisk, 2) // 警戒区域は中リスク
      }
    }
    // その他の土砂災害
    else {
      maxRisk = Math.max(maxRisk, 1) // 低リスク
    }
  }

  return maxRisk
}

/**
 * 分析ポリゴンの色をリスクレベルに応じて更新
 * @param map MapLibre map instance
 * @param id ポリゴンのID
 * @param riskLevel リスクレベル (0-3)
 */
export function updatePolygonColor(map: any, id: string, riskLevel: number): void {
  const polygon = displayedPolygons.get(id)
  if (!polygon) return

  const colors = getRiskColors(riskLevel)

  // プロパティに色を追加
  const updatedPolygon = {
    ...polygon,
    properties: {
      ...polygon.properties,
      fillColor: colors.fillColor,
      lineColor: colors.lineColor,
      riskLevel,
    },
  }

  displayedPolygons.set(id, updatedPolygon as Feature<Polygon>)

  if (map && map.getSource(POLYGON_SOURCE_ID)) {
    updatePolygonSource(map)
  }
}

/**
 * 分析ポリゴンの塗り透明度を更新
 * @param map MapLibre map instance
 * @param id ポリゴンのID
 * @param fillOpacity 0-1
 */
export function updatePolygonFillOpacity(map: any, id: string, fillOpacity: number): void {
  const polygon = displayedPolygons.get(id)
  if (!polygon) return

  const updatedPolygon = {
    ...polygon,
    properties: {
      ...polygon.properties,
      fillOpacity,
    },
  }

  displayedPolygons.set(id, updatedPolygon as Feature<Polygon>)

  if (map && map.getSource(POLYGON_SOURCE_ID)) {
    updatePolygonSource(map)
  }
}

/**
 * 分析ポリゴンのラベルに人口を追加
 * @param map MapLibre map instance
 * @param id ポリゴンのID
 * @param population 人口総数
 */
export function updatePolygonPopulation(map: any, id: string, population: number): void {
  // 人口を別Mapに保存（updatePolygonSourceで使用）
  polygonPopulations.set(id, population)

  if (map && map.getSource(POLYGON_SOURCE_ID)) {
    updatePolygonSource(map)
  }
}

/**
 * 分析ポリゴンレイヤーを最前面に移動
 * レイヤー追加後に呼び出してポリゴンが隠れないようにする
 */
export function bringPolygonLayersToFront(map: any): void {
  if (!map) return

  // ソースが存在しない場合は初期化
  if (!map.getSource(POLYGON_SOURCE_ID)) {
    initPolygonLayers(map)
  }

  // ソースデータを再設定（消えている場合の復元）
  if (displayedPolygons.size > 0) {
    updatePolygonSource(map)
  }

  // レイヤーが存在する場合のみ移動
  const layerIds = [POLYGON_FILL_LAYER_ID, POLYGON_LINE_LAYER_ID, POLYGON_LABEL_LAYER_ID]
  for (const layerId of layerIds) {
    if (map.getLayer(layerId)) {
      map.moveLayer(layerId)
    } else {
      // レイヤーがない場合は再初期化
      initPolygonLayers(map)
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId)
      }
    }
  }
}

// ============================================================
// サークル描画
// ============================================================

/**
 * 円半径ツールチップを表示
 */
function showCircleRadiusTooltip(event: MouseEvent | TouchEvent, radius: number): void {
  let tooltip = document.getElementById('area-analysis-circle-tooltip')
  if (!tooltip) {
    tooltip = document.createElement('div')
    tooltip.id = 'area-analysis-circle-tooltip'
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(156, 39, 176, 0.9);
      color: white;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: bold;
      pointer-events: none;
      z-index: 10000;
      transform: translate(10px, -50%);
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `
    document.body.appendChild(tooltip)
  }

  let clientX: number, clientY: number
  if ('touches' in event && event.touches.length > 0) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    clientX = (event as MouseEvent).clientX
    clientY = (event as MouseEvent).clientY
  }

  tooltip.textContent = `半径 ${radius}m`
  tooltip.style.left = `${clientX}px`
  tooltip.style.top = `${clientY}px`
  tooltip.style.display = 'block'
}

/**
 * 円半径ツールチップを非表示
 */
function hideCircleRadiusTooltip(): void {
  const tooltip = document.getElementById('area-analysis-circle-tooltip')
  if (tooltip) {
    tooltip.style.display = 'none'
  }
}

interface CircleState {
  active: boolean
  center: [number, number] | null
  map: any
  canvas: HTMLCanvasElement | null
  resolve: ((polygon: Feature<Polygon> | null) => void) | null
  handlers: {
    click: ((e: any) => void) | null
    mousemove: ((e: any) => void) | null
    contextmenu: ((e: any) => void) | null
  }
}

const circleState: CircleState = {
  active: false,
  center: null,
  map: null,
  canvas: null,
  resolve: null,
  handlers: { click: null, mousemove: null, contextmenu: null },
}

/**
 * AI防災リスク診断専用サークル描画を開始
 * @returns Promise<Feature<Polygon> | null> - 描画されたポリゴン、またはキャンセル時はnull
 */
export function startAreaAnalysisCircle(map: any): Promise<Feature<Polygon> | null> {
  return new Promise((resolve) => {
    if (circleState.active) {
      stopAreaAnalysisCircle()
    }

    circleState.active = true
    circleState.center = null
    circleState.map = map
    circleState.canvas = map.getCanvas()
    circleState.resolve = resolve

    initPreviewLayer(map)

    const onClick = (e: any) => {
      if (!circleState.active) return

      if (!circleState.center) {
        // 1回目のクリック: 中心を決定
        circleState.center = [e.lngLat.lng, e.lngLat.lat]
        map.getCanvas().classList.add('is-area-analysis-circle-radius')
      } else {
        // 2回目のクリック: 半径を決定してポリゴンを作成
        const radiusM = turf.distance(
          turf.point(circleState.center),
          turf.point([e.lngLat.lng, e.lngLat.lat]),
          { units: 'meters' }
        )

        if (radiusM < 10) {
          // 半径が小さすぎる場合はキャンセル
          stopAreaAnalysisCircle(true)
          resolve(null)
          return
        }

        const circleGeoJson = turf.circle(circleState.center, radiusM / 1000, {
          steps: 64,
          units: 'kilometers',
        })

        const polygon: Feature<Polygon> = {
          type: 'Feature',
          geometry: circleGeoJson.geometry as Polygon,
          properties: {
            isCircle: true,
            radiusM: Math.round(radiusM),
            center: circleState.center,
          },
        }

        hideCircleRadiusTooltip()
        stopAreaAnalysisCircle(true)
        resolve(polygon)
      }
    }

    const onMouseMove = (e: any) => {
      if (!circleState.active || !circleState.center) return

      const radiusM = turf.distance(
        turf.point(circleState.center),
        turf.point([e.lngLat.lng, e.lngLat.lat]),
        { units: 'meters' }
      )

      const circleGeoJson = turf.circle(circleState.center, radiusM / 1000, {
        steps: 64,
        units: 'kilometers',
      })

      const source = map.getSource(PREVIEW_SOURCE_ID)
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: [circleGeoJson],
        })
      }

      // ツールチップを表示
      showCircleRadiusTooltip(e.originalEvent, Math.round(radiusM))
    }

    const onContextMenu = (e: any) => {
      e.preventDefault()
      stopAreaAnalysisCircle(true)
      resolve(null)
    }

    circleState.handlers.click = onClick
    circleState.handlers.mousemove = onMouseMove
    circleState.handlers.contextmenu = onContextMenu

    map.on('click', onClick)
    map.on('mousemove', onMouseMove)
    map.getCanvas().addEventListener('contextmenu', onContextMenu)

    map.getCanvas().classList.add('is-area-analysis-circle')
  })
}

/**
 * AI防災リスク診断専用サークル描画を停止
 * @param skipResolve trueの場合はresolve(null)を呼ばない（正常完了時用）
 */
export function stopAreaAnalysisCircle(skipResolve = false): void {
  if (!circleState.active) return

  const { map, handlers } = circleState

  if (map && handlers.click) {
    map.off('click', handlers.click)
  }
  if (map && handlers.mousemove) {
    map.off('mousemove', handlers.mousemove)
  }
  if (circleState.canvas && handlers.contextmenu) {
    circleState.canvas.removeEventListener('contextmenu', handlers.contextmenu)
  }

  if (map) {
    clearPreview(map)
    map.getCanvas().classList.remove('is-area-analysis-circle')
    map.getCanvas().classList.remove('is-area-analysis-circle-radius')
  }

  hideCircleRadiusTooltip()

  // Promiseをresolve（ESCキーでのキャンセル時のみ）
  if (!skipResolve && circleState.resolve) {
    circleState.resolve(null)
  }

  circleState.active = false
  circleState.center = null
  circleState.map = null
  circleState.canvas = null
  circleState.handlers = { click: null, mousemove: null, contextmenu: null }
  circleState.resolve = null
}

// ============================================================
// ポリゴン描画
// ============================================================

interface PolygonDrawState {
  active: boolean
  coords: [number, number][]
  map: any
  canvas: HTMLCanvasElement | null
  resolve: ((polygon: Feature<Polygon> | null) => void) | null
  handlers: {
    click: ((e: any) => void) | null
    mousemove: ((e: any) => void) | null
    dblclick: ((e: any) => void) | null
    contextmenu: ((e: any) => void) | null
  }
}

const polygonDrawState: PolygonDrawState = {
  active: false,
  coords: [],
  map: null,
  canvas: null,
  resolve: null,
  handlers: { click: null, mousemove: null, dblclick: null, contextmenu: null },
}

/**
 * AI防災リスク診断専用ポリゴン描画を開始
 * @returns Promise<Feature<Polygon> | null> - 描画されたポリゴン、またはキャンセル時はnull
 */
export function startAreaAnalysisPolygon(map: any): Promise<Feature<Polygon> | null> {
  return new Promise((resolve) => {
    if (polygonDrawState.active) {
      stopAreaAnalysisPolygon()
    }

    polygonDrawState.active = true
    polygonDrawState.coords = []
    polygonDrawState.map = map
    polygonDrawState.canvas = map.getCanvas()
    polygonDrawState.resolve = resolve

    initPreviewLayer(map)

    const updatePreview = (cursorCoord?: [number, number]) => {
      const source = map.getSource(PREVIEW_SOURCE_ID)
      if (!source) return

      const coords = [...polygonDrawState.coords]
      if (cursorCoord) {
        coords.push(cursorCoord)
      }

      if (coords.length < 2) {
        source.setData({ type: 'FeatureCollection', features: [] })
        return
      }

      const features: any[] = []

      // ラインを表示
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
        properties: {},
      })

      // 3点以上なら仮ポリゴンを表示
      if (coords.length >= 3) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[...coords, coords[0]]],
          },
          properties: {},
        })
      }

      // 頂点を表示
      for (const coord of polygonDrawState.coords) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coord },
          properties: {},
        })
      }

      source.setData({ type: 'FeatureCollection', features })
    }

    const onClick = (e: any) => {
      if (!polygonDrawState.active) return

      const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat]

      // 最初の点に近い場合は閉じる
      if (polygonDrawState.coords.length >= 3) {
        const firstCoord = polygonDrawState.coords[0]
        const dist = turf.distance(turf.point(coord), turf.point(firstCoord), { units: 'meters' })
        if (dist < 20) {
          finishPolygon()
          return
        }
      }

      polygonDrawState.coords.push(coord)
      updatePreview()
    }

    const onMouseMove = (e: any) => {
      if (!polygonDrawState.active) return
      updatePreview([e.lngLat.lng, e.lngLat.lat])
    }

    const onDblClick = (e: any) => {
      if (!polygonDrawState.active) return
      e.preventDefault()
      finishPolygon()
    }

    const onContextMenu = (e: any) => {
      e.preventDefault()
      stopAreaAnalysisPolygon(true)
      resolve(null)
    }

    let isFinishing = false

    const finishPolygon = () => {
      // 既に終了処理中の場合は無視（ダブルクリック時の重複呼び出し防止）
      if (isFinishing) return

      // active状態でない場合も無視
      if (!polygonDrawState.active) return

      isFinishing = true

      if (polygonDrawState.coords.length < 3) {
        stopAreaAnalysisPolygon(true)
        resolve(null)
        return
      }

      const ring = [...polygonDrawState.coords, polygonDrawState.coords[0]]
      const polygon: Feature<Polygon> = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [ring],
        },
        properties: {},
      }

      stopAreaAnalysisPolygon(true)
      resolve(polygon)
    }

    polygonDrawState.handlers.click = onClick
    polygonDrawState.handlers.mousemove = onMouseMove
    polygonDrawState.handlers.dblclick = onDblClick
    polygonDrawState.handlers.contextmenu = onContextMenu

    map.on('click', onClick)
    map.on('mousemove', onMouseMove)
    map.on('dblclick', onDblClick)
    map.getCanvas().addEventListener('contextmenu', onContextMenu)

    map.getCanvas().classList.add('is-area-analysis-polygon')
    map.doubleClickZoom.disable()
  })
}

/**
 * AI防災リスク診断専用ポリゴン描画を停止
 */
/**
 * @param skipResolve trueの場合はresolve(null)を呼ばない（正常完了時用）
 */
export function stopAreaAnalysisPolygon(skipResolve = false): void {
  if (!polygonDrawState.active) return

  const { map, handlers } = polygonDrawState

  if (map && handlers.click) {
    map.off('click', handlers.click)
  }
  if (map && handlers.mousemove) {
    map.off('mousemove', handlers.mousemove)
  }
  if (map && handlers.dblclick) {
    map.off('dblclick', handlers.dblclick)
  }
  if (polygonDrawState.canvas && handlers.contextmenu) {
    polygonDrawState.canvas.removeEventListener('contextmenu', handlers.contextmenu)
  }

  if (map) {
    clearPreview(map)
    map.getCanvas().classList.remove('is-area-analysis-polygon')
    map.doubleClickZoom.enable()
  }

  // Promiseをresolve（ESCキーでのキャンセル時のみ）
  if (!skipResolve && polygonDrawState.resolve) {
    polygonDrawState.resolve(null)
  }

  polygonDrawState.active = false
  polygonDrawState.coords = []
  polygonDrawState.map = null
  polygonDrawState.canvas = null
  polygonDrawState.handlers = { click: null, mousemove: null, dblclick: null, contextmenu: null }
  polygonDrawState.resolve = null
}
